// =============================================================================
// Stranded Deep風ゲームレイヤー — A方式（threejswaterproミラーを無改変で土台にする）
// index.html の observe フックが捕捉した window.__game.{scene,camera,renderer} を使い、
// ミラーの描画カメラを一人称プレイヤーが乗っ取る。ミラー本体には一切触れない。
//   実装機能: 一人称で泳ぐ / 島に上陸 / 素材採取 / クラフト / 体力・空腹
// =============================================================================
import { SEA_LEVEL, WORLD_PER_M, toM, findIsland, SHIP } from './world';
import { buildIslandHeightfield, buildFloorField, groundHeight } from './terrain';
import { placeResourceNodes, nearestNode, gatherNode, ensureTypesNearSpawn } from './resources';
import { buildIslandResources, type IslandResources } from './island-assets';
import { buildMountainIsland } from './mountain';
import { spawnRaft, spawnCampfire, spawnWaterStill, spawnShelter, spawnShark, spawnCrate, spawnHeldItem, spawnFishFloat, spawnFishLine, spawnBubbleBurst } from './mesh3d';
import { stats, tickSurvival, canSprint, isExhausted, damage } from './survival';
import { add, consume, craft, inventory, ITEMS, RECIPES } from './crafting';
import { clock, tickTime, clockLabel, phaseLabel, timeOfDay } from './time';
import './debug-menu'; // 視認性テスト用デバッグメニュー( ` キーで開閉)
import { FEATS, loadFeats, unlockFeat, unlockedCount, isUnlocked } from './milestones';
import { saveState, loadState, applyState, clearSave } from './save';
import * as hud from './hud';
import { initAudio, setAmbience, sfx } from './audio';
import type { Heightfield, ResourceNode, Player } from './types';

const game = window.__game;
const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0; // 文言のタッチ対応用

// --- 物理定数（世界は大スケール=実寸の約10倍。目線16m=身長1.6m相当。[ ]で増減可）----
// 速度/重力/ジャンプは身長(eyeHeight)に比例 → 目線を変えても歩き/走り/跳躍の体感が一致する。
const EYE = 32, RUN_MUL = 2.6, SEABED_MIN = -240;  // 目線32m(身長2倍)。海底の底=12m(クレート最深-120に余裕で届く)
const WALK_K = 20.0;  // 走行速度 = 目線×20 /s（10倍）
const SWIM_K = 17.0;  // 遊泳速度（10倍）
const GRAV_K = 2.4;   // 重力
const JUMP_K = 1.15;  // ジャンプ初速
const swimDepth = () => player.eyeHeight - 0.5; // 遊泳時は目線が水面+0.5に出る沈み込み

// --- プレイヤー -------------------------------------------------------------
const player: Player = {
  pos: { x: 200, y: 0, z: 300 },
  yaw: 0, pitch: -0.03, vy: 0,
  eyeHeight: EYE,
  mode: 'swim',          // 'swim' | 'dive' | 'land' | 'fall' | 'dead'
  grounded: false,
  keys: Object.create(null),
  tmove: { x: 0, y: 0 },
  enabled: true,
  frozen: false,         // テスト用: trueで物理停止（カメラはプレイヤー位置に固定）
  _bob: 0,
  _shake: 0,
  teleport(x: number, y: number, z: number) { player.pos.x = x; player.pos.y = y; player.pos.z = z; player.vy = 0; },
  lookAt(tx: number, ty: number, tz: number) {
    const dx = tx - player.pos.x, dy = ty - player.pos.y, dz = tz - player.pos.z;
    const h = Math.hypot(dx, dz) || 1e-6;
    player.yaw = Math.atan2(-dx, -dz);
    player.pitch = Math.max(-1.5, Math.min(1.5, Math.atan2(dy, h)));
  },
};
window.__player = player;

let hf: Heightfield | null = null;   // 島 heightfield
let nodes: ResourceNode[] = [];      // 資源ノード
let islandRes: IslandResources | null = null; // 実アセット採取（椰子→木/草→繊維/岩→石）

// --- 複数の島 ---------------------------------------------------------------
// メイン島のGroupを丸ごとclone(GLB由来ジオメトリ=Safari安全)し、平行移動(xz+y)で沖に配置。
// yを上下させて「高台の島 / 浅い環礁」のバリエーションを出す。
// 【制約】島の地形はTSLノード材質(world空間計算)のため、クローンに回転・スケールを掛けると
//   地形が崩壊する(検証済み)。平行移動のみが安全。
const ISLAND_OFFSETS: { dx: number; dz: number; dy: number; name: string }[] = [
  { dx: 0, dz: 0, dy: 0, name: '漂着島' },
  { dx: 3400, dz: 2200, dy: 55, name: '南東の高島' },   // 持ち上げ=切り立った高い島
  { dx: -3800, dz: 1600, dy: -35, name: '南西の環礁' }, // 沈める=平たい浅瀬の島
];
// 自前地形の島(山岳島など): 専用Heightfieldを持つ
const customIslands: { hf: Heightfield; floorHf: Heightfield; cx: number; cz: number; name: string }[] = [];
const MT = { cx: 1400, cz: -4600, name: '北の山岳島' }; // 北の沖に配置
// 大きな山の島と大きな島(自前地形=数式から描画と足場を同一生成)。
// 「小さい島と大きい島がある世界」: 水平線の向こうに見える巨大な山(峰1300=実寸65m、山岳島の4倍)と、
// 歩き回れる広大な島(半径2200=メイン島の4倍超)。falloffのphaseで一面をなだらかに=徒歩で登頂できる。
const BIG_ISLES = [
  { cx: 4300, cz: -2200, name: '東の天峰島', R: 1600, peak: 2200, seed: 11, grid: 160, hfN: 288,
    falloff: { base: 1.22, swing: 0.06, phase: 0.8 }, ridgeAmp: 0.10, noiseScale: 0.0012, noiseTaper: 0.72, detailAmp: 0.04, shelfStart: 0.90, colors: 'mountain' as const,
    rocks: 14, palms: 3, grass: 4, rockMinH: 55 },
  { cx: -3500, cz: -3200, name: '北西の大島', R: 2400, peak: 240, seed: 23, grid: 192, hfN: 256,
    falloff: { base: 1.05, swing: 0.28, phase: 3.5 }, ridgeAmp: 0.16, noiseScale: 0.0025, colors: 'green' as const,
    rocks: 8, palms: 10, grass: 8, rockMinH: 25 },
];

// 全島に対する接地高さ: クローン島(オフセット+dy) と 自前地形島(専用hf) の最大
function groundAt(x: number, z: number): number {
  let best = -Infinity;
  for (const o of ISLAND_OFFSETS) {
    const h = groundHeight(hf, x - o.dx, z - o.dz);
    if (h === -Infinity) continue;
    if (h + o.dy > best) best = h + o.dy;
  }
  for (const ci of customIslands) {
    const h = groundHeight(ci.hf, x, z);
    if (h > best) best = h;
  }
  return best;
}
(window as any).__groundAt = groundAt; // 診断: 接地高さの直接サンプル(平坦な陸探し等)

// (x,z)がどの島の圏内かを島名で返す(圏外はnull)。判定は「島の半径で正規化した距離」で行い、
// 大きい島(大島R2400等)ほど広い圏を持つ。クローン島の実半径≈550に対し圏1000、島間の
// 最短距離は3900超なので重なりは構造的に起きない。初上陸演出とレア素材の島判定に使う。
function islandAt(x: number, z: number): string | null {
  let best: string | null = null, bestScore = 1;
  for (const o of ISLAND_OFFSETS) {
    const d = Math.hypot(x - (500 + o.dx), z - (-500 + o.dz)) / 1000;
    if (d < bestScore) { bestScore = d; best = o.name; }
  }
  for (const ci of customIslands) {
    const R = (ci.hf.bounds.maxX - ci.hf.bounds.minX) / 2; // 自前地形は生成時の実半径
    const d = Math.hypot(x - ci.cx, z - ci.cz) / Math.max(1000, R * 1.1);
    if (d < bestScore) { bestScore = d; best = ci.name; }
  }
  return best;
}

// --- 実績(マイルストーン): 達成の瞬間を金色バナー+ファンファーレで祝う ------------------
// 実績は側6-featsキーで周回(はじめから)をまたいで永続する。バナーはhud側のキューで順次表示。
function award(id: string): void {
  const def = unlockFeat(id);
  if (!def || titleMode) return; // タイトル画面中は無演出(unlock自体は永続化済み)
  sfx('feat');
  hud.showFeat(def, unlockedCount(), FEATS.length);
}
let visitedIslands: string[] = [];  // 上陸済みの島名(PROG_KEYに保存。はじめからでリセット=再発見を楽しめる)
let rareFound: string[] = [];       // 入手歴のあるレア素材(消費しても達成が残るようinventoryと別管理)
let forceRare = false;              // テスト用: レアドロップを確定させる
// 島ごとの固有レア素材: その島で採取していると稀に見つかる(島を渡る動機)
const RARE_BY_ISLAND: Record<string, string> = { '南東の高島': 'bloom', '南西の環礁': 'pearl', '北の山岳島': 'obsidian' };
const RARE_CHANCE = 0.18;
const RARE_USE: Record<string, string> = { pearl: '📿真珠の御守の材料だ', obsidian: '🗡銛を強化できる', bloom: '🍵癒しの茶になる' };
// レア素材入手の演出+入手歴(addは呼び出し側)。金色トースト+キラキラ音で「見つけた！」を祝う
function foundRare(item: string): void {
  if (!rareFound.includes(item)) { rareFound.push(item); requestSave(); } // 在庫と対で保存(進行ロス防止の規律)
  sfx('rare');
  hud.toast(`✨ ${ITEMS[item]?.name ?? item}を見つけた！ — ${RARE_USE[item] ?? '島の秘宝だ'}`, 'rare');
  award('first_rare');
  if (['pearl', 'obsidian', 'bloom'].every((r) => rareFound.includes(r))) award('rare_all');
}

// 潜水の床: 裾野つきheightfield(見た目の棚の縁から-240へ連続的に下る)。配置ロジックはgroundAtのまま。
let hfFloor: Heightfield | null = null;
function floorAt(x: number, z: number): number {
  let best = -Infinity;
  for (const o of ISLAND_OFFSETS) {
    const h = groundHeight(hfFloor, x - o.dx, z - o.dz);
    if (h === -Infinity) continue;
    if (h + o.dy > best) best = h + o.dy;
  }
  for (const ci of customIslands) {
    const h = groundHeight(ci.floorHf, x, z);
    if (h > best) best = h;
  }
  return best;
}
(window as any).__floorAt = floorAt; // 診断: 潜水床の直接サンプル

// --- 難破船の船体衝突(OBB) ----------------------------------------------------
// ミラーの船体メッシュには当たり判定が無く、真下から浮上すると竜骨を突き抜けて船内に
// 入れた(モバイル実機報告)。泳ぎ/潜水の目線位置がhullローカルAABB内なら最短面から外へ
// 押し出す。上面へは出さない(甲板に乗せない)。船スケール15なので余白はローカル換算。
let shipHull: any = null;
function initShipCollision(): void {
  game.scene.traverse((o: any) => { if (!shipHull && o.isMesh && o.name === 'dutch_ship_medium_hull') shipHull = o; });
  if (shipHull && !shipHull.geometry.boundingBox) shipHull.geometry.computeBoundingBox();
}

// --- 外洋の見える海底(ミラーの砂平面 12800², y=-100)を潜水限界の下へ --------------------
// 潜水の底は SEABED_MIN(-240) なのに見える砂は -100 にあり、外洋のどこでも「見えている砂を
// 突き抜けて裏側を見上げる」構造欠陥の根本原因だった(海溝の財宝-226も砂の下)。平面を
// SEABED_MIN-5 へ平行移動し(ミラー無改変の原則=位置だけ動かす)、以後この世界の不変条件を
// 「見える底(砂平面) ≤ 潜れる底(SEABED_MIN) ≤ 全ての衝突床」にする。
// ミラーは天気プリセット切替などで海を再構築し平面が作り直される(=一度動かしても戻る)ため、
// tickから定期的(1.5s)に呼んで実測yを確認し、ズレていれば補正し続ける。
let oceanPlane: any = null;
let nextFloorEnforce = 0;
function enforceOceanFloorPlane(): void {
  const measure = (o: any): number => {
    const bb = o.geometry.boundingBox;
    const v = o.position.clone().set((bb.min.x + bb.max.x) / 2, (bb.min.y + bb.max.y) / 2, (bb.min.z + bb.max.z) / 2);
    o.localToWorld(v);
    return v.y;
  };
  if (!oceanPlane || !oceanPlane.parent) {
    oceanPlane = null;
    game.scene.traverse((o: any) => {
      if (oceanPlane || !(o.isMesh && o.geometry)) return;
      if (!o.geometry.boundingBox) { try { o.geometry.computeBoundingBox(); } catch { return; } }
      const bb = o.geometry.boundingBox; if (!bb) return;
      const s = o.getWorldScale ? o.getWorldScale(o.position.clone()).x : 1;
      const w = (bb.max.x - bb.min.x) * s, h = (bb.max.y - bb.min.y) * s;
      if (!(w > 10000 && w < 20000 && h < 1)) return;
      const y = measure(o);
      if (Math.abs(y + 100) < 8 || Math.abs(y - (SEABED_MIN - 5)) < 8) oceanPlane = o;
    });
    if (!oceanPlane) return; // まだ生成前(起動直後/再構築中)。次の周期で拾う
  }
  const y = measure(oceanPlane);
  const target = SEABED_MIN - 5;
  if (Math.abs(y - target) > 0.5) {
    oceanPlane.position.y += target - y;
    oceanPlane.updateMatrixWorld(true);
    console.log('[game] ocean floor plane lowered: %f → %d', +y.toFixed(1), target);
  }
  // 平面に生えていた海底デコ(海草/石/小難破船=scene直下のInstancedMesh、全インスタンスが
  // 旧平面の-100.2に固定)も一緒に下ろす。放置すると平面だけ沈んで中層に浮いて見える。
  // 平面同様プリセット切替で作り直されるため毎周期で実測して補正する。
  let moved = 0;
  for (const c of game.scene.children) {
    if (!(c.isInstancedMesh && c.instanceMatrix?.array?.length >= 16)) continue;
    const worldY = c.position.y + c.instanceMatrix.array[13]; // 先頭インスタンスのy(デコは全インスタンス同一高)
    if (Math.abs(worldY + 100.2) > 8) continue; // 旧平面の高さにあるものだけ=他のインスタンス群(魚等)は触らない
    c.position.y += (target - 0.2) - worldY; // 平面より0.2下=根元が砂に埋まる従来の位置関係を維持
    c.updateMatrixWorld(true);
    moved++;
  }
  if (moved) console.log('[game] seabed deco lowered with floor plane: %d meshes', moved);
}
// タッチ端末の発熱対策: 太陽(DirectionalLight)のシャドウマップを4096→2048へ縮小する。
// 4096²は深度付きで毎フレーム約1680万pxのラスタ+約130MBのGPUメモリを占め、QUALITY=lowでも
// 縮まない全品質共通の固定負荷だった(iPhone発熱の主要因の一つ)。2048²でラスタ/メモリとも1/4。
// スマホ画面ではスクショ比較で差が知覚できないことを確認済み(_shadow-probe.mjs)。
// 天気プリセット切替で空・ライトが再構築され4096に戻り得るため、床enforceと同周期で再適用する。
function enforceMobileShadowMapSize(): void {
  if (!IS_TOUCH) return;
  game.scene.traverse((o: any) => {
    const sh = o.isLight && o.castShadow && o.shadow;
    if (!sh || !sh.mapSize || sh.mapSize.width <= 2048) return;
    sh.mapSize.set(2048, 2048);
    if (sh.map?.setSize) sh.map.setSize(2048, 2048);
    sh.camera?.updateProjectionMatrix?.();
    console.log('[game] sun shadow map shrunk to 2048 (mobile heat)');
  });
}
// 反射/屈折系の補助RT(CSSビューポートサイズ固定でpixelRatio非追従)をcanvas実ピクセルへ合わせる。
// pr=0.5時これらは4パス合計でメイン描画の16倍超のピクセルを毎フレームラスタし、QUALITYを
// 下げても縮まない(iPhone発熱の主要因の一つ、実測=_pass-census.mjs)。縮小は「そのRTへ描画する
// 直前」(renderフック内)に行う: 書き込み前なら同フレームの後続の読み手は新テクスチャを参照する。
// ※外側のタイマーから一括resizeすると、非同期animateのawait中に割り込んで書き込み済みRTを
//   破棄してしまい、同フレームのサンプル側が mipLevelCount エラーで死ぬ(実測済みの罠)。
// 画面回転やプリセット再構築でCSSサイズのRTが現れても、この述語が再び合致して自動で縮む。
// pr=1ではcanvas実サイズ=CSSサイズで条件不成立(デスクトップ既定は無変更)。視覚差なしを
// _rt-shrink-scenes.mjs(浜/水面/水中)のA/Bで確認済み。
function installAuxRTDownscaler(): void {
  const r: any = game.renderer;
  if (!r || r.__auxRTWrapped) return;
  r.__auxRTWrapped = true;
  const orig = r.render.bind(r);
  r.render = (scene: any, camera: any) => {
    const rt = r.getRenderTarget && r.getRenderTarget();
    if (rt && camera && camera.isPerspectiveCamera && rt.width === window.innerWidth && rt.height === window.innerHeight) {
      const tw = r.domElement.width, th = r.domElement.height;
      if (tw > 0 && th > 0 && tw < window.innerWidth) { try { rt.setSize(tw, th); } catch { /* 破棄済みRT */ } }
    }
    return orig(scene, camera);
  };
}
function resolveShipCollision(): void {
  if (!shipHull) return;
  const bb = shipHull.geometry.boundingBox;
  const v = shipHull.position.clone().set(player.pos.x, player.pos.y + EYE, player.pos.z);
  shipHull.worldToLocal(v);
  if (v.x <= bb.min.x || v.x >= bb.max.x || v.y <= bb.min.y || v.y >= bb.max.y || v.z <= bb.min.z || v.z >= bb.max.z) return;
  const M = 5 / 15; // 余白 world≈5
  const cand: { d: number; ap: () => void }[] = [
    { d: v.x - bb.min.x, ap: () => { v.x = bb.min.x - M; } },
    { d: bb.max.x - v.x, ap: () => { v.x = bb.max.x + M; } },
    { d: v.z - bb.min.z, ap: () => { v.z = bb.min.z - M; } },
    { d: bb.max.z - v.z, ap: () => { v.z = bb.max.z + M; } },
    { d: v.y - bb.min.y, ap: () => { v.y = bb.min.y - M; player.vy = Math.min(player.vy, 0); } },
  ];
  cand.sort((a, b2) => a.d - b2.d);
  cand[0].ap();
  shipHull.localToWorld(v);
  player.pos.x = v.x; player.pos.z = v.z; player.pos.y = v.y - EYE;
}
let rafts: any[] = [];               // 配置済みのいかだ(THREE.Group)。近づくと乗れる。
let riding: any = null;              // 現在乗っているいかだ（null=乗っていない）
const RAFT_DECK = 5.4;               // いかだ甲板の高さ(world, size1)。buildRaftの deckTop=R*2 と一致。
// いかだの喫水オフセット(world)。波高計(ブイ)のyはブイ喫水バイアス≈-4.5を含む(実測平均-4.25)ため、
// +2.2 で「丸太の2/3が水面上」の自然な浮きになる。大きくしすぎると宙に浮いて見える。
const RAFT_FLOAT = 2.2;

// --- いかだの波追従(本物の波) ---------------------------------------------------
// ミラーの BuoyancySystem に登録済みのブイ(12個)は毎フレーム「その位置の波高とうねりの傾き」が
// position.y / quaternion に書かれる“生きた波高計”。1台につき1個を拝借して非表示化し、
// いかだの真下へ追従させて y と pitch/roll をコピーする(FFT波に完全同期・ミラー無改変)。
function acquireWaveProbe(): any {
  for (const o of game.scene.children) {
    if ((o as any).__gameProbe) continue;
    let isBuoy = false;
    o.traverse((m: any) => { if (m.isMesh && m.name === 'buoy') isBuoy = true; });
    if (!isBuoy) continue;
    (o as any).__gameProbe = true;
    o.visible = false; // 景観のブイとしては消す(残り11個が海上に残る)
    return o;
  }
  return null; // 12台以上は借りられない→フェイク揺れにフォールバック
}

// --- テスト/外部API ---------------------------------------------------------
window.__survival = stats;
window.__inv = inventory;
(window as any).__refreshHud = () => { hud.updateHotbar(); hud.refreshCraft(); }; // デバッグ: inv直接編集後にHUD再描画
window.__nodes = () => nodes;
(window as any).__aabbOf = (id: number) => (islandRes ? islandRes.aabbOf(id) : null); // デバッグ: 採取当たり判定AABB
window.__interact = doGather;
(window as any).__doInteract = () => interact();
(window as any).__repairState = () => ({ ...repair, remaining: repairRemaining(), escaped });
(window as any).__doRepair = () => doRepair();
(window as any).__shark = () => ({ state: shark.state, danger: +shark.danger.toFixed(1), dist: Math.round(shark.dist), hp: shark.hp, visible: !!shark.obj?.visible, deep: playerInDeepWater() });
(window as any).__attack = () => { attackCooldown = 0; attack(); };
(window as any).__forceShark = () => { shark.danger = SHARK_SPAWN_SEC + 1; }; // テスト: 次tickでサメ出現
(window as any).__threatMul = () => threatMul();
(window as any).__boss = () => ({ crateId: boss.crateId, active: boss.active, hp: boss.hp, dead: boss.dead, visible: !!boss.obj?.visible, pos: boss.obj ? [Math.round(boss.obj.position.x), Math.round(boss.obj.position.z)] : null });
(window as any).__sharksKilled = () => gameStats.sharksKilled;
(window as any).__fish = () => ({ fishing: +fishing.toFixed(1), bite: +fishBite.toFixed(2), can: canFish() });
(window as any).__crates = () => crates.map((c) => ({ id: c.id, x: Math.round(c.x), y: Math.round(c.y), z: Math.round(c.z), opened: c.opened, loot: c.loot, tier: c.tier || 'normal' }));
(window as any).__nearestCrate = () => { const c = nearestCrate(); return c ? { id: c.id, dist: Math.round(Math.hypot(c.x - player.pos.x, c.y - player.pos.y, c.z - player.pos.z)) } : null; };
(window as any).__gotoCrate = (id: number) => { const c = crates.find((k) => k.id === id); if (!c) return false; player.teleport(c.x, c.y + 8, c.z); player.mode = 'dive'; return true; };
(window as any).__startFishing = () => startFishing();
(window as any).__weather = () => ({ raining: weather.raining, timer: Math.round(weather.timer) });
(window as any).__feats = () => ({ unlocked: FEATS.filter((f) => isUnlocked(f.id)).map((f) => f.id), count: unlockedCount(), total: FEATS.length });
(window as any).__forceRare = (on: boolean) => { forceRare = !!on; }; // テスト: レアドロップを確定に
(window as any).__visited = () => [...visitedIslands];
(window as any).__rares = () => [...rareFound];
(window as any).__islandAt = (x: number, z: number) => islandAt(x, z);
(window as any).__flotsam = () => (flotsam ? { id: flotsam.id, x: Math.round(flotsam.x), z: Math.round(flotsam.z), opened: flotsam.opened } : null);
(window as any).__spawnFlotsam = () => { spawnMorningFlotsam(); return (window as any).__flotsam(); }; // テスト: 朝を待たず漂着物を湧かす
(window as any).__forceLetter = () => triggerLetter(); // テスト: 手紙を即発動
(window as any).__relics = () => ({ next: relicNext, spots: relicSpots.length, active: crates.filter((c) => c.tier === 'relic').map((c) => ({ id: c.id, y: Math.round(c.y), opened: c.opened })) });
(window as any).__titleState = () => ({ titleMode, frozen: player.frozen });
(window as any).__setRain = (on: boolean, kind?: 'storm' | 'foggy') => { weather.raining = on; if (on && kind) weather.kind = kind; weather.timer = on ? 60 : 200; hud.setRain(on); };
window.__eat = () => consume('coconut');
window.__craftItem = (id: string) => {
  // 設置型(焚き火/給水器)は陸上でのみクラフト可能で、その場(足元)に設置される
  const placeable = id === 'campfire' || id === 'water_still' || id === 'shelter';
  if (placeable && !(groundAt(player.pos.x, player.pos.z) > SEA_LEVEL)) { hud.toast('陸の上でしか作れない'); return false; }
  // 調理(station=campfire)は焚き火のそばでのみ(給水器等が「最寄り」でもリーチ内に焚き火があればよい)
  const recipe = RECIPES.find((r) => r.id === id);
  if (recipe?.station === 'campfire' && !nearestPlaced('campfire')) { hud.toast('🔥 焚き火のそばで調理しよう'); return false; }
  const ok = craft(id);
  if (ok && recipe?.station === 'campfire') {
    sfx('rest');
    hud.toast(id === 'flower_tea' ? '🍵 花の癒し茶 を淹れた' : '🍖 ' + (ITEMS[recipe.out.item]?.name ?? id) + ' を焼いた');
    hud.updateHotbar(); hud.refreshCraft();
    award('first_cook');
    return ok;
  }
  if (!ok) return false;
  gameStats.crafted += 1;
  award('first_craft');
  if (placeable) {
    inventory[id] -= 1; // インベントリには持たずその場に設置
    placeObject(id as 'campfire' | 'water_still' | 'shelter', player.pos.x, player.pos.y, player.pos.z, player.yaw);
    sfx('place');
    hud.toast(id === 'campfire' ? '🔥 焚き火を設置した' : id === 'shelter' ? '🛖 シェルターを建てた——我が家だ' : '💧 給水器を設置した');
    // 生活基盤: 焚き火(休息・調理)と給水器(真水)が両方揃った=夜も渇きも怖くない節目
    if (placed.some((p) => p.type === 'campfire') && placed.some((p) => p.type === 'water_still')) award('camp_ready');
    if (id === 'shelter') award('home');
  } else {
    sfx('craft');
    hud.toast('🔨 ' + (ITEMS[id]?.name ?? id) + ' を作った');
  }
  hud.updateHotbar(); hud.refreshCraft();
  { const r2 = RECIPES.find((r) => r.id === id); if (r2) hud.flashSlot(r2.out.item); } // 作った物のスロットが光る
  if ((inventory['axe'] || 0) > 0 && (inventory['stone_knife'] || 0) > 0 && hasSpear() && (inventory['fishing_rod'] || 0) > 0) award('tool_master');
  return ok;
};
window.__state = () => ({
  ready: window.__gameReady === true,
  mode: player.mode,
  pos: [+player.pos.x.toFixed(1), +player.pos.y.toFixed(1), +player.pos.z.toFixed(1)],
  ground: hf ? +groundAt(player.pos.x, player.pos.z).toFixed(1) : null,
  health: +stats.health.toFixed(1), hunger: +stats.hunger.toFixed(1),
  thirst: +stats.thirst.toFixed(1), stamina: +stats.stamina.toFixed(1), alive: stats.alive,
  inventory: { ...inventory }, nodeCount: nodes.length,
  nearNode: (() => { const n = nearestNode(nodes, player.pos.x, player.pos.y, player.pos.z, player.eyeHeight / 8); return n ? n.def.label : null; })(),
});
window.__save = () => { const ok = saveState(player, nodes, repair, placedData()); if (ok) saveProgress(); return ok; }; // 手動保存はstate+progressの完全対
// 設定パネルの「手動セーブ」。自動セーブと同じ安全条件(タイトル/死亡/脱出/潜水中は不可)+結果をトーストで返す
window.__manualSave = () => {
  if (titleMode || !stats.alive || escaped) { hud.toast('💾 いまはセーブできない'); return false; }
  if (player.mode === 'dive') { hud.toast('💾 潜水中はセーブできない — 浮上してから'); return false; }
  const ok = saveState(player, nodes, repair, placedData());
  if (ok) { saveProgress(); lastSave = performance.now(); hud.toast('💾 セーブした'); }
  else hud.toast('💾 セーブに失敗した…');
  return ok;
};
window.__load = () => { const d = loadState(); if (d) { applyState(d, player, nodes, repair); restorePlaced(d.placed); syncAssets(); } return !!d; };
window.__clearSave = clearSave;
window.__time = clock;
window.__restart = restart;
window.__addRock = (x: number, z: number) => islandRes?.addRock(x, z) ?? null;
window.__addPalm = (x: number, z: number, type: 'wood' | 'fiber' = 'wood') => islandRes?.addPalm(x, z, type) ?? null;
(window as any).__addRaft = (x: number, z: number, size?: number) => { // いかだを(x,z)海面に配置(実いかだと同じ: 乗船可+波追従)
  const r = spawnRaft(game.scene, x, z, { size, y: RAFT_FLOAT });
  if (r) { (r as any).__probe = acquireWaveProbe(); rafts.push(r); }
  return r;
};
(window as any).__placeRaft = () => placeRaft();
(window as any).__board = () => tryBoard();
(window as any).__dismount = () => dismount();
(window as any).__riding = () => !!riding;
(window as any).__raftInfo = () => rafts.map((r) => ({ x: +r.position.x.toFixed(1), z: +r.position.z.toFixed(1) }));

// セーブ復元後、枯渇(amount<=0)した資源のアセットを畳んで「アセット＝リソースの存在」を一致させる。
function syncAssets(): void { if (islandRes) for (const n of nodes) if (n.amount <= 0) islandRes.removeObject(n.id); }

function restart(): void { clearSave(); location.reload(); }

// --- 入力 -------------------------------------------------------------------
function setupInput(): void {
  // 入力オーバーレイ: ミラーのカメラ操作(Orbit/LMBドラッグ)にポインタを奪われないよう、
  // canvasの上に透明な最前面レイヤを敷いて、こちらでポインタロックを取得する。
  const input = document.createElement('div');
  input.id = 'game-input';
  input.style.cssText = 'position:fixed;inset:0;z-index:5000;cursor:crosshair;';
  document.body.appendChild(input);
  // ローディング画面(#initial-loading, z-index:10000)は opacity:0 になった後も pointer-events を
  // 持ったままマウス入力を奪い、Chrome等で「最初の数十秒カメラlookが効かない」原因になる。
  // ゲーム開始時に無効化して入力を #game-input / HUD へ通す。
  const il = document.getElementById('initial-loading');
  if (il) (il as HTMLElement).style.pointerEvents = 'none';
  const isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  // pointer lock の取得は mousedown（押下時）で1回。
  // click だと「押しながら動かす(=ドラッグ)」操作では click が発火せず取得できない（ドラッグ視点は効くのにロックだけ入れない症状になる）。
  // 押下は移動を伴っても有効な取得ジェスチャなので mousedown で取る。二重要求を避けるため click では取得しない。
  // 取得できない間（後述）はドラッグ視点でフォールバックし、次の押下で再取得を試みる（失敗は握りつぶす）。
  //
  // 【既知の注意】Chrome DevTools を開いている（フォーカスを持っている）と、pointer lock の
  //   「ウィンドウがブラウザ/OSレベルでフォーカスされていること」要件を満たさず、requestPointerLock が
  //   WrongDocumentError("The root document of this element is not valid for pointer lock.") で拒否される。
  //   document.hasFocus() が true でも発生する。DevTools を閉じる、または DevTools の
  //   Rendering → "Emulate a focused page" を有効にすると最初からロックできる。実ユーザー（DevTools非使用）は無影響。
  const lock = () => {
    if (isTouch || !player.enabled || hud.uiOpen() || document.pointerLockElement || !input.requestPointerLock) return;
    const r: any = input.requestPointerLock(); if (r && typeof r.catch === 'function') r.catch(() => {});
  };

  const lookDelta = (mx: number, my: number) => {
    const s = 0.0022;
    player.yaw -= mx * s; player.pitch -= my * s;
    const lim = Math.PI / 2 - 0.02; player.pitch = Math.max(-lim, Math.min(lim, player.pitch));
  };
  // pointer lock 中: マウス移動デルタで視点を回す
  document.addEventListener('mousemove', (e) => {
    if (document.pointerLockElement == null) return;
    lookDelta(e.movementX, e.movementY);
  }, { capture: true });
  // フォールバック: pointer lock が取れない間（Chrome起動直後のWebGPUシェーダ大量コンパイル中など）でも
  // オーバーレイ上のドラッグで視点を回せるようにする。これで「最初の数十秒カメラが動かない」を回避。
  let dragging = false, lastX = 0, lastY = 0;
  input.addEventListener('mousedown', (e) => {
    dragging = true; lastX = e.clientX; lastY = e.clientY; lock();
    // 銛所持時の左クリック=突き攻撃(未所持なら通常のドラッグ視点/ロック取得のみ)
    if (e.button === 0 && player.enabled && !hud.uiOpen()) useEquipped();
  });
  addEventListener('mouseup', () => { dragging = false; });
  input.addEventListener('mousemove', (e) => {
    if (document.pointerLockElement != null || !dragging || !player.enabled || hud.uiOpen()) return;
    lookDelta(e.clientX - lastX, e.clientY - lastY); // clientの差分で自前計算(movementX非依存)
    lastX = e.clientX; lastY = e.clientY;
  });
  addEventListener('keydown', (e) => {
    player.keys[e.code] = true;
    if (e.code === 'KeyE') interact();
    if (/^Digit[1-9]$/.test(e.code)) { // 数字キーでホットバーの装備切替
      const idx = Number(e.code.slice(5)) - 1;
      const items = Object.keys(inventory).filter((k) => inventory[k] > 0);
      if (items[idx]) equip(items[idx]);
    }
    if (e.code === 'KeyR') placeRaft();
    if (e.code === 'KeyQ') startFishing();
    // クイック食事: 調理済み・ココナッツを小さい順に(満腹+85を空腹-10で食べる浪費を防ぐ)。生の魚/サメ肉は調理素材なので最後
    if (e.code === 'KeyF') { if (consume('coconut') || consume('cooked_fish') || consume('cooked_meat') || consume('fish') || consume('shark_meat')) { sfx('eat'); hud.updateHotbar(); } }
    if (e.code === 'KeyC') {
      const open = hud.toggleCraft();
      if (open && document.exitPointerLock) document.exitPointerLock();
    }
    if (e.code === 'KeyV') { // 実績一覧(達成/未達成と次の目標が見える)
      const open = hud.toggleFeats();
      if (open && document.exitPointerLock) document.exitPointerLock();
    }
    if (e.code === 'BracketLeft') { player.eyeHeight = Math.max(2, player.eyeHeight - 1); hud.toast('目線 ' + (player.eyeHeight / WORLD_PER_M).toFixed(2) + 'm'); }
    if (e.code === 'BracketRight') { player.eyeHeight = Math.min(40, player.eyeHeight + 1); hud.toast('目線 ' + (player.eyeHeight / WORLD_PER_M).toFixed(2) + 'm'); }
  }, { capture: true });
  addEventListener('keyup', (e) => { player.keys[e.code] = false; }, { capture: true });
  addEventListener('blur', () => { for (const k in player.keys) player.keys[k] = false; });
}

// モバイル: 左半分=移動ジョイスティック / 右半分=視点 / アクションボタン
function setupTouch(): void {
  const input = document.getElementById('game-input');
  if (!input) return;
  const isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

  // ジョイスティック表示
  const stick = document.createElement('div');
  stick.style.cssText = 'position:fixed;width:130px;height:130px;border-radius:50%;border:2px solid rgba(255,255,255,.3);background:rgba(0,0,0,.18);z-index:6000;pointer-events:none;display:none;transform:translate(-50%,-50%);';
  const knob = document.createElement('div');
  knob.style.cssText = 'position:absolute;left:65px;top:65px;width:56px;height:56px;border-radius:50%;background:rgba(255,255,255,.5);transform:translate(-50%,-50%);';
  stick.appendChild(knob);
  document.body.appendChild(stick);

  let moveId = -1, moveOX = 0, moveOY = 0;
  let lookId = -1, lookLX = 0, lookLY = 0;
  const RAD = 60, SENS = 0.005;

  // タッチ識別子の孤児化対策（iOS恒久フリーズの根本対策）:
  // iOSはシステムジェスチャや高負荷時に touchend/touchcancel をページへ配送し損ねることがある。
  // 取りこぼすと moveId/lookId が永久に解放されず、以降の新規タッチが全分岐から弾かれて
  // 「移動も視点も二度と効かない（リロードでしか回復しない）」恒久フリーズになる。
  // 対策: 毎イベントで現存タッチ集合(e.touches)を正とし、そこに居ないIDは強制解放する。
  // これで取りこぼしが起きても、次のタッチ時に自己回復する。
  const idLive = (e: TouchEvent, id: number): boolean => {
    for (const t of Array.from(e.touches)) if (t.identifier === id) return true;
    return false;
  };
  const reconcile = (e: TouchEvent): void => {
    if (moveId >= 0 && !idLive(e, moveId)) { moveId = -1; player.tmove.x = 0; player.tmove.y = 0; stick.style.display = 'none'; }
    if (lookId >= 0 && !idLive(e, lookId)) { lookId = -1; }
  };

  const onStart = (e: TouchEvent) => {
    reconcile(e);
    for (const t of Array.from(e.changedTouches)) {
      if (t.clientX < window.innerWidth / 2 && moveId < 0) {
        moveId = t.identifier; moveOX = t.clientX; moveOY = t.clientY;
        stick.style.left = moveOX + 'px'; stick.style.top = moveOY + 'px'; stick.style.display = 'block';
        knob.style.left = '65px'; knob.style.top = '65px';
      } else if (lookId < 0) {
        lookId = t.identifier; lookLX = t.clientX; lookLY = t.clientY;
      }
    }
    e.preventDefault();
  };
  const onMove = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier === moveId) {
        let dx = t.clientX - moveOX, dy = t.clientY - moveOY;
        const len = Math.hypot(dx, dy);
        if (len > RAD) { dx = dx / len * RAD; dy = dy / len * RAD; }
        player.tmove.x = dx / RAD; player.tmove.y = dy / RAD;
        knob.style.left = (65 + dx) + 'px'; knob.style.top = (65 + dy) + 'px';
      } else if (t.identifier === lookId) {
        const dx = t.clientX - lookLX, dy = t.clientY - lookLY;
        lookLX = t.clientX; lookLY = t.clientY;
        player.yaw -= dx * SENS; player.pitch -= dy * SENS;
        const lim = Math.PI / 2 - 0.02; player.pitch = Math.max(-lim, Math.min(lim, player.pitch));
      }
    }
    e.preventDefault();
  };
  const onEnd = (e: TouchEvent) => {
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier === moveId) { moveId = -1; player.tmove.x = 0; player.tmove.y = 0; stick.style.display = 'none'; }
      if (t.identifier === lookId) { lookId = -1; }
    }
  };
  input.addEventListener('touchstart', onStart, { passive: false });
  input.addEventListener('touchmove', onMove, { passive: false });
  input.addEventListener('touchend', onEnd);
  input.addEventListener('touchcancel', onEnd);

  if (!isTouch) return;
  // モバイル用アクションボタン
  const bar = document.createElement('div');
  bar.id = 'touch-actions';
  // 縦一列だと画面右端を丸ボタンが埋め尽くすので、親指の届く右下に2列グリッドでまとめる。
  bar.style.cssText = 'position:fixed;right:calc(12px + env(safe-area-inset-right, 0px));bottom:calc(78px + env(safe-area-inset-bottom, 0px));z-index:9100;display:grid;grid-template-columns:repeat(2,54px);gap:8px;justify-items:center;'; // ノッチ/ホームバー退避(viewport-fit=cover時のみ非0)
  document.body.appendChild(bar);
  // accent: 用途で淡く色分け(移動=青 / 攻撃=赤 / 採取=緑)。それ以外は無彩色。
  const ACCENT: Record<string, string> = { move: 'rgba(46,120,190,.66)', atk: 'rgba(190,70,55,.66)', get: 'rgba(60,150,90,.66)' };
  const mkBtn = (label: string, accent?: string): HTMLElement => {
    const b = document.createElement('div');
    b.textContent = label;
    const bg = accent ? ACCENT[accent] : 'rgba(8,20,32,.62)';
    b.style.cssText = `width:54px;height:54px;border-radius:50%;background:${bg};box-shadow:0 0 0 1px rgba(255,255,255,.22) inset,0 2px 8px rgba(0,0,0,.35);color:#eaf2f8;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;user-select:none;touch-action:none;`;
    bar.appendChild(b);
    return b;
  };
  const tap = (b: HTMLElement, fn: () => void) => b.addEventListener('touchstart', (e) => { e.preventDefault(); fn(); }, { passive: false });
  const hold = (b: HTMLElement, code: string) => {
    b.addEventListener('touchstart', (e) => { e.preventDefault(); player.keys[code] = true; }, { passive: false });
    b.addEventListener('touchend', () => { player.keys[code] = false; });
    b.addEventListener('touchcancel', () => { player.keys[code] = false; });
  };
  // グリッドは行ごとに左→右へ充填。右列に「浮上→潜る」を縦に並べ、上=浮上/下=潜るの直感に合わせる。
  // 装備システム: 道具が増えてもボタンは増えない。「使う」=装備品のアクション(ホットバーで装備切替)
  tap(mkBtn('作'), () => { const open = hud.toggleCraft(); if (open && document.exitPointerLock) document.exitPointerLock(); }); // (1,1)
  const riseBtn = mkBtn('浮上', 'move'); hold(riseBtn, 'Space'); // (1,2) 上。陸上ではジャンプなのでラベルを出し分ける
  window.setInterval(() => { const t = (player.mode === 'land' || player.mode === 'fall') ? '跳ぶ' : '浮上'; if (riseBtn.textContent !== t) riseBtn.textContent = t; }, 400);
  tap(mkBtn('調べる', 'get'), () => interact());     // (2,1) 文脈アクション(採取/乗る/開ける/修理/休む/飲む)
  hold(mkBtn('潜る', 'move'), 'ControlLeft');        // (2,2) 下 = 浮上の真下
  tap(mkBtn('使う', 'atk'), () => useEquipped());    // (3,1) 装備品を使う(突く/釣る/置く/食べる)
  tap(mkBtn('記録'), () => hud.toggleFeats());       // (3,2) 実績一覧(グリッド最後の空きマス)
}

// --- 採取 -------------------------------------------------------------------
// 照準で「見ている」資源を選ぶ。視線(前方)ベクトルに最も合致し、採取距離内のノードを返す。
// 見ているものが無ければ密着時の最寄りにフォールバック。
const GATHER_REACH = 200; // world(≈10m)
function targetNode(): ResourceNode | null {
  // 視線(前方)ベクトル。原点=目線(カメラ)位置。
  const cp = Math.cos(player.pitch), sp = Math.sin(player.pitch), sy = Math.sin(player.yaw), cy = Math.cos(player.yaw);
  const fx = -cp * sy, fy = sp, fz = -cp * cy;
  const ox = player.pos.x, oy = player.pos.y + player.eyeHeight, oz = player.pos.z;
  // オブジェクト全体(AABB)に視線レイを当てて、当たったうち最も近いものを採取対象に。
  if (islandRes) { const hit = islandRes.pick(ox, oy, oz, fx, fy, fz, GATHER_REACH); if (hit) return hit; }
  // 当たらなくても密着(≈1.5m)なら最寄りを採れる
  let near: ResourceNode | null = null, nd = 30 * 30;
  for (const n of nodes) {
    if (n.amount <= 0) continue;
    const dx = n.x - player.pos.x, dz = n.z - player.pos.z; const d2 = dx * dx + dz * dz;
    if (d2 < nd && Math.abs(n.y - player.pos.y) < 60) { nd = d2; near = n; }
  }
  return near;
}

function doGather(): boolean {
  if (!player.enabled || !stats.alive) return false;
  const n = targetNode();
  if (!n) return false;
  const got = gatherNode(n);
  if (!got) return false;
  // 道具ボーナス: 石斧=木材+1 / 石ナイフ=繊維+1（道具を作る意味を持たせる）
  for (const g of got) {
    if (g.item === 'wood' && (inventory['axe'] || 0) > 0) g.count += 1;
    if (g.item === 'fiber' && (inventory['stone_knife'] || 0) > 0) g.count += 1;
  }
  for (const g of got) add(g.item, g.count);
  if (n.amount <= 0 && islandRes) islandRes.removeObject(n.id); // 採取しきったら実3Dを消す
  stats.hunger = Math.max(0, stats.hunger - 0.6); // 採取で少し消耗
  gameStats.gathered += 1;
  player._shake = 0.022; // ヒットの手応え(カメラが一瞬ぶれる)
  heldSwing = HELD_SWING_SEC; // 手元の道具(斧など)を振るアニメ
  sfx('gather'); sfx('pickup');
  hud.updateHotbar(); hud.refreshCraft();
  hud.toast('🪶 ' + got.map((g) => `+${g.count} ${ITEMS[g.item]?.name ?? g.item}`).join('  '));
  // 島の秘宝: 漂着島以外の島で採取していると、その島の固有レア素材が稀に見つかる
  {
    const isl = islandAt(n.x, n.z);
    const rare = isl ? RARE_BY_ISLAND[isl] : null;
    if (rare && (forceRare || Math.random() < RARE_CHANCE)) {
      add(rare, 1);
      foundRare(rare);
      hud.updateHotbar(); hud.refreshCraft();
      hud.flashSlot(rare);
    }
  }
  return true;
}

// --- いかだ: 配置・搭乗 ------------------------------------------------------
// クラフトした「いかだ床」を消費して、目の前の水面へ実いかだ(spawnRaft)を配置する。
function placeRaft(): boolean {
  if (!player.enabled || !stats.alive) return false;
  if ((inventory['raft_floor'] || 0) <= 0) { hud.toast('🛶 いかだ床が足りない（Cでクラフト）'); return false; }
  const fx = -Math.sin(player.yaw), fz = -Math.cos(player.yaw); // 前方(水平)
  const dist = 150; // 前方 ≈7.5m（近すぎると足元で見えないので離す）
  const x = player.pos.x + fx * dist, z = player.pos.z + fz * dist;
  const r = spawnRaft(game.scene, x, z, { size: 1, y: RAFT_FLOAT, yaw: player.yaw });
  if (!r) { hud.toast('いかだの配置に失敗した'); return false; }
  (r as any).__probe = acquireWaveProbe(); // 波追従用の波高計を拝借
  rafts.push(r);
  placed.push({ type: 'raft', x, y: RAFT_FLOAT, z, obj: r, water: 0, yaw: player.yaw }); // セーブ対象(リロードで復元)
  inventory['raft_floor'] -= 1;
  hud.updateHotbar(); hud.refreshCraft();
  sfx('place');
  hud.toast(IS_TOUCH ? '🛶 いかだを配置した（近づいて「調べる」で乗る）' : '🛶 いかだを配置した（近づいて E で乗る）');
  return true;
}
(window as any).__placeRaft = placeRaft; // 診断: いかだ設置→セーブ復元の検証用

// 搭乗可能な最寄りのいかだ（甲板の広さ+リーチ内）を返す。
function nearestRaft(): any {
  let best: any = null, bd = Infinity;
  for (const r of rafts) {
    const size = r.scale?.x || 1;
    const reach = 40 * size + 30; // 甲板半径 + 乗り込みリーチ
    const dx = r.position.x - player.pos.x, dz = r.position.z - player.pos.z;
    const d2 = dx * dx + dz * dz;
    if (d2 <= reach * reach && d2 < bd) { bd = d2; best = r; }
  }
  return best;
}

function tryBoard(): boolean {
  const r = nearestRaft();
  if (!r) return false;
  riding = r;
  r.rotation.y = player.yaw; // 乗った瞬間から船首=自分の向き(帆が正面を塞がない)
  sfx('board');
  hud.toast(IS_TOUCH ? '🛶 いかだに乗った（左スティックで移動 / 「調べる」で降りる）' : '🛶 いかだに乗った（WASDで移動 / Eで降りる）');
  award('first_sail');
  return true;
}

function dismount(): void {
  if (!riding) return;
  riding = null; player.vy = 0; player.mode = 'swim';
  sfx('board');
  hud.toast('🛶 いかだから降りた');
}

// --- 脱出目標: 難破船の修理 ---------------------------------------------------
// 沖に停泊する難破船(SHIP)に素材を運んで修理し、全て揃えると脱出(クリア)。
const REPAIR_NEED: Record<string, number> = { plank: 12, rope: 8, fiber: 12 };
const repair: Record<string, number> = {};
let escaped = false;
const REPAIR_REACH = 420; // 船は巨大なので届く範囲は広め(≈21m)

function repairRemaining(): number {
  let s = 0;
  for (const k in REPAIR_NEED) s += Math.max(0, REPAIR_NEED[k] - (repair[k] || 0));
  return s;
}
function repairLabel(): string {
  return Object.keys(REPAIR_NEED)
    .map((k) => `${ITEMS[k]?.icon ?? k}${repair[k] || 0}/${REPAIR_NEED[k]}`)
    .join(' ');
}
function nearShip(): boolean {
  const dx = player.pos.x - SHIP.x, dz = player.pos.z - SHIP.z;
  return dx * dx + dz * dz < REPAIR_REACH * REPAIR_REACH;
}
// 手持ちから必要素材を投入。何か投入できたらtrue。
function doRepair(): boolean {
  if (!nearShip() || escaped) return false;
  let poured = 0;
  for (const k in REPAIR_NEED) {
    const need = REPAIR_NEED[k] - (repair[k] || 0);
    const use = Math.min(need, inventory[k] || 0);
    if (use > 0) { inventory[k] -= use; repair[k] = (repair[k] || 0) + use; poured += use; }
  }
  if (poured > 0) {
    sfx('repair');
    hud.updateHotbar(); hud.refreshCraft();
    hud.toast(`🔧 船を修理した (${repairLabel()})`);
    // 修理50%到達の節目(必要総数に対する投入済み比率)
    let needSum = 0, haveSum = 0;
    for (const k in REPAIR_NEED) { needSum += REPAIR_NEED[k]; haveSum += Math.min(REPAIR_NEED[k], repair[k] || 0); }
    if (haveSum * 2 >= needSum) award('repair_half');
  } else if (repairRemaining() > 0) {
    // 何がいくつ足りないか+入手手段まで示す(「持っていない」だけでは次の行動が分からない)
    const miss = Object.keys(REPAIR_NEED)
      .map((k) => ({ k, n: REPAIR_NEED[k] - (repair[k] || 0) }))
      .filter((m) => m.n > 0)
      .map((m) => `${ITEMS[m.k]?.icon ?? m.k}${m.n}`).join(' ');
    hud.toast(`🔧 素材が足りない… あと ${miss}（板=木2・ロープ=繊維2でクラフト）`);
  }
  if (repairRemaining() <= 0 && !escaped) {
    escaped = true;
    unlockFeat('escape'); // 生還は勝利画面が最大の祝福なのでバナーは出さず記録のみ(統計に実績n/20が載る)
    sfx('win');
    player.frozen = true;
    clearSave();
    // 出航演出: 船を見送れる位置へ下がり、船はtickでゆっくり出航していく
    escapedShip = (() => { let s2: any = null; game.scene.traverse((o: any) => { if (!s2 && o.name === 'dutch_ship_medium_hull') { let a = o; while (a.parent && a.parent !== game.scene) a = a.parent; s2 = a; } }); return s2; })();
    if (escapedShip) {
      player.teleport(escapedShip.position.x + 420, 30, escapedShip.position.z + 260);
      player.lookAt(escapedShip.position.x, 20, escapedShip.position.z);
    }
    hud.showVictory(clock.day, statsText(), () => restart(), escapeRank(clock.day));
    if (document.exitPointerLock) document.exitPointerLock();
  }
  return poured > 0;
}

// --- 設置物: 焚き火(休息) / 給水器(貯水→飲む) ---------------------------------
// クラフトするとその場(足元)に設置される。焚き火=朝まで休んで回復、給水器=時間で水が貯まる。
interface Placed { type: 'campfire' | 'water_still' | 'shelter' | 'raft'; x: number; y: number; z: number; obj: any; water: number; yaw?: number }
const placed: Placed[] = [];
const WATER_RATE = 1 / 300;    // 貯水速度: 1杯/300秒(ゲーム内半日)
const WATER_MAX = 3;           // 最大3杯
const PLACED_REACH = 60;       // ≈3m

function placeObject(type: 'campfire' | 'water_still' | 'shelter', x: number, y: number, z: number, yaw = 0): Placed | null {
  const obj = type === 'campfire' ? spawnCampfire(game.scene, x, y, z)
    : type === 'shelter' ? spawnShelter(game.scene, x, y, z, yaw)
    : spawnWaterStill(game.scene, x, y, z);
  if (!obj) return null;
  const p: Placed = { type, x, y, z, obj, water: 0, yaw };
  placed.push(p);
  return p;
}
function nearestPlaced(type?: string): Placed | null {
  let best: Placed | null = null, bd = PLACED_REACH * PLACED_REACH;
  for (const p of placed) {
    if (type && p.type !== type) continue;
    const dx = p.x - player.pos.x, dz = p.z - player.pos.z;
    const d2 = dx * dx + dz * dz;
    if (d2 < bd && Math.abs(p.y - player.pos.y) < 80) { bd = d2; best = p; }
  }
  return best;
}
// セーブ用の設置物データ / 復元(メッシュ再生成)
function placedData(): { type: string; x: number; y: number; z: number; water: number; yaw?: number }[] {
  return placed.map((p) => ({ type: p.type, x: p.x, y: p.y, z: p.z, water: p.water, yaw: p.yaw }));
}
function restorePlaced(data?: { type: string; x: number; y: number; z: number; water: number; yaw?: number }[]): void {
  if (!data) return;
  for (const d of data) {
    if (d.type === 'raft') { // クラフトしたいかだも復元(旧: 消滅して素材だけ失われていた)
      const r = spawnRaft(game.scene, d.x, d.z, { size: 1, y: RAFT_FLOAT, yaw: d.yaw || 0 });
      if (r) {
        (r as any).__probe = acquireWaveProbe();
        rafts.push(r);
        placed.push({ type: 'raft', x: d.x, y: RAFT_FLOAT, z: d.z, obj: r, water: 0, yaw: d.yaw });
      }
      continue;
    }
    if (d.type !== 'campfire' && d.type !== 'water_still' && d.type !== 'shelter') continue;
    const p = placeObject(d.type, d.x, d.y, d.z, d.yaw || 0);
    if (p) p.water = d.water || 0;
  }
}

// 朝まで眠る共通処理(焚き火/シェルター)。給水器の夜間貯水・翌朝6時・雨上がりは共通、回復量が違う。
function sleepToMorning(kind: 'campfire' | 'shelter'): void {
  sfx('rest');
  hud.fadeTransition(() => {
    // 眠っている間も給水器は貯まる(スキップ時間ぶん・就寝時の天候レート)。雨の夜に休むと朝は満タン
    const skipped = 1.25 * clock.dayLength - clock.t;
    const stillGain = WATER_RATE * (weather.raining ? RAIN_STILL_MUL : 1) * skipped;
    let stillReady = false; // 起床時に1杯以上あるときだけ知らせる(0.7杯等の飲めない量では言わない)
    for (const p of placed) if (p.type === 'water_still') { p.water = Math.min(WATER_MAX, p.water + stillGain); if (p.water >= 1) stillReady = true; }
    clock.day += 1; clock.t = 0.25 * clock.dayLength; // 翌朝6:00
    // 雨は一晩で上がる: 天候は実時間タイマーのため、放置すると50〜120秒のスコールが「7時間の睡眠」を跨いで続いてしまう
    if (weather.raining) { weather.raining = false; weather.timer = 150 + Math.random() * 240; }
    if (kind === 'shelter') {
      stats.health = stats.maxHealth; // 我が家=ぐっすり眠れて全回復(消耗も軽い)
      stats.hunger = Math.max(5, stats.hunger - 10);
      stats.thirst = Math.max(5, stats.thirst - 14);
    } else {
      stats.health = Math.min(stats.maxHealth, stats.health + 30);
      stats.hunger = Math.max(5, stats.hunger - 15);
      stats.thirst = Math.max(5, stats.thirst - 20);
    }
    stats.stamina = stats.maxStamina;
    hud.updateStats(stats);
    const msg = kind === 'shelter' ? '🛖 我が家でぐっすり眠った（体力全回復）' : '🔥 焚き火のそばで朝まで休んだ';
    hud.toast(msg + (stillReady ? '　💧給水器に水が貯まっている' : ''));
  });
}
// 焚き火で休む: 暗転してから翌朝6時へ(回復+少し消耗)
function restAtFire(): void { sleepToMorning('campfire'); }
function drinkFromStill(p: Placed): void {
  if (p.water < 1) { hud.toast('💧 まだ水が貯まっていない…'); return; }
  p.water -= 1;
  stats.thirst = Math.min(stats.maxThirst, stats.thirst + 45);
  sfx('drink');
  hud.updateStats(stats);
  hud.toast('💧 淡水を飲んだ');
}

// --- 海中サルベージ: 沈没クレート ------------------------------------------------
// 各島の周囲の海底(潜水で届く深さ)に物資クレートが眠る。潜って E で開封→物資回収。
// 潜水に「目的と報酬」を与え、修理素材のグラインドを緩和し、発見の喜びを生む。
interface Crate { id: number; x: number; y: number; z: number; obj: any; loot: { item: string; count: number }[]; opened: boolean; tier?: 'treasure' | 'flotsam' | 'relic' }
const crates: Crate[] = [];
const CRATE_REACH = 52;        // 開封リーチ(≈2.6m。潜水しながら届く)
let openedCrates: number[] = []; // セーブ済みの開封済みid(復元時に再開封を防ぐ)
let salvageHintShown = false;  // 初回の潜水誘導トーストを一度だけ出すためのフラグ
let vistaUntil = 0;            // 山頂の見晴らし: この時刻まで全沈没物資をコンパス表示(登山の報酬)

// ルート抽選(決定論rng)。修理素材(板/ロープ/繊維)中心 + たまに魚 + レアでまとまった物資。
function crateLoot(rnd: () => number): { item: string; count: number }[] {
  const r = rnd();
  if (r < 0.30) return [{ item: 'rope', count: 1 + Math.floor(rnd() * 2) }];
  if (r < 0.58) return [{ item: 'plank', count: 1 + Math.floor(rnd() * 2) }];
  if (r < 0.74) return [{ item: 'fiber', count: 2 + Math.floor(rnd() * 3) }];
  if (r < 0.88) return [{ item: 'fish', count: 1 + Math.floor(rnd() * 2) }];
  return [{ item: 'plank', count: 2 }, { item: 'rope', count: 1 }]; // レア: 物資の塊
}

// 財宝(深海set-piece)のルート。脱出素材のまとまった塊=深海へ潜る強い動機。決定論で個体差。
function treasureLoot(rnd: () => number): { item: string; count: number }[] {
  const bonus = Math.floor(rnd() * 2); // 0/1で個体差
  return [
    { item: 'plank', count: 3 + bonus },
    { item: 'rope', count: 2 },
    { item: 'fiber', count: 3 },
  ];
}

// 各島の周囲の海底(海面下20〜120world=潜水で届く棚)にクレートを決定論散布。
function placeCrates(): void {
  let s = 913571; const rnd = () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  const anchors: { cx: number; cz: number; name: string }[] = [];
  for (const o of ISLAND_OFFSETS) anchors.push({ cx: 500 + o.dx, cz: -500 + o.dz, name: o.name });
  for (const ci of customIslands) anchors.push({ cx: ci.cx, cz: ci.cz, name: ci.name });
  let id = 0;
  for (const a of anchors) {
    let n = 0;
    for (let tries = 0; tries < 260 && n < 2; tries++) {
      const ang = rnd() * Math.PI * 2, rad = 320 + rnd() * 720;
      const x = a.cx + Math.cos(ang) * rad, z = a.cz + Math.sin(ang) * rad;
      const sea = groundAt(x, z);
      if (sea === -Infinity || sea > SEA_LEVEL - 20 || sea < SEA_LEVEL - 120) continue; // 潜水で届く海底のみ
      if (crates.some((c) => Math.hypot(c.x - x, c.z - z) < 200)) continue;             // 過密回避
      const y = sea + 5; // 海底に少し埋まった位置
      const obj = spawnCrate(game.scene, x, y, z, { seed: id + 1 });
      if (!obj) break;
      const c: Crate = { id, x, y, z, obj, loot: crateLoot(rnd), opened: false };
      if (openedCrates.includes(id)) { c.opened = true; obj.visible = false; }
      crates.push(c);
      id++; n++;
    }
  }
  // 財宝(深海set-piece): 各島棚の最深部(海面下85〜115=潜水限界近く)に大型の財宝を1つ。
  // サメが出る深さ=銛で戦う覚悟が要る高報酬。コンパスに💎ビーコン常時表示で「目標」になる。
  for (const a of anchors) {
    let bestDeep: { x: number; z: number; y: number } | null = null;
    for (let tries = 0; tries < 400; tries++) {
      const ang = rnd() * Math.PI * 2, rad = 360 + rnd() * 760;
      const x = a.cx + Math.cos(ang) * rad, z = a.cz + Math.sin(ang) * rad;
      const sea = groundAt(x, z);
      if (sea === -Infinity || sea > SEA_LEVEL - 85 || sea < SEA_LEVEL - 115) continue; // 潜水限界近くの深い棚
      if (crates.some((c) => Math.hypot(c.x - x, c.z - z) < 220)) continue;
      if (!bestDeep || sea < bestDeep.y) bestDeep = { x, z, y: sea };       // より深い所を採用
    }
    if (!bestDeep) continue;
    const y = bestDeep.y + 6;
    const obj = spawnCrate(game.scene, bestDeep.x, y, bestDeep.z, { seed: id + 101 });
    if (!obj) continue;
    obj.scale.set(1.7, 1.7, 1.7); // 大型=財宝の風格
    const loot = treasureLoot(rnd);
    if (RARE_BY_ISLAND[a.name]) loot.push({ item: RARE_BY_ISLAND[a.name], count: 1 }); // 島の秘宝が確定で眠る(採取運に頼らない入手ルート)
    const c: Crate = { id, x: bestDeep.x, y, z: bestDeep.z, obj, loot, opened: false, tier: 'treasure' };
    if (openedCrates.includes(id)) { c.opened = true; obj.visible = false; }
    crates.push(c);
    id++;
  }
  // ボス「主(ヌシ)」の海溝: 全島から離れた外洋の最深部(海底-240近く)に主の財宝を置く。
  // 島の斜面と違い周囲に地形が無い=巨体が常に見え、暗く深い水が「海溝の主」の凄みを出す。
  {
    const TRENCH = { x: 500, z: 3200 }; // メイン島の真南の外洋(全島から2km超・実測ground=-Infinity)
    const y = SEABED_MIN;               // 砂平面(SEABED_MIN-5)に半埋まり=潜水限界の底で待つ最も深い挑戦
    const obj = spawnCrate(game.scene, TRENCH.x, y, TRENCH.z, { seed: id + 101 });
    if (obj) {
      obj.scale.set(1.9, 1.9, 1.9);
      const c: Crate = {
        id, x: TRENCH.x, y, z: TRENCH.z, obj, opened: false, tier: 'treasure',
        loot: [ // 主の財宝は別格(脱出素材のほぼ半分)
          { item: 'plank', count: 6 }, { item: 'rope', count: 4 }, { item: 'fiber', count: 6 },
        ],
      };
      boss.crateId = id;
      if (openedCrates.includes(id)) { c.opened = true; obj.visible = false; }
      crates.push(c);
      id++;
    }
  }
  console.log('[game] crates placed: %d (treasure: %d, boss=%d)', crates.length, crates.filter((c) => c.tier === 'treasure').length, boss.crateId);
}

// 大きな島々のクレート散布。既存セーブとの互換のためid空間を分離(通常100+/財宝200+)。
// 条件はplaceCratesと同じ(棚=海面下20〜120に通常2個、最深部85〜115に財宝1個)だが、
// 散布半径は島の大きさ(bi.R)に比例させる。
function placeBigIsleCrates(): void {
  let s = 777001; const rnd = () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  let id = 100;
  for (const bi of BIG_ISLES) {
    let n = 0;
    for (let tries = 0; tries < 300 && n < 2; tries++) {
      const ang = rnd() * Math.PI * 2, rad = bi.R * 0.80 + rnd() * bi.R * 0.36;
      const x = bi.cx + Math.cos(ang) * rad, z = bi.cz + Math.sin(ang) * rad;
      const sea = groundAt(x, z);
      if (sea === -Infinity || sea > SEA_LEVEL - 20 || sea < SEA_LEVEL - 120) continue;
      if (crates.some((c) => Math.hypot(c.x - x, c.z - z) < 200)) continue;
      const y = sea + 5;
      const obj = spawnCrate(game.scene, x, y, z, { seed: id + 1 });
      if (!obj) break;
      const c: Crate = { id, x, y, z, obj, loot: crateLoot(rnd), opened: false };
      if (openedCrates.includes(id)) { c.opened = true; obj.visible = false; }
      crates.push(c);
      id++; n++;
    }
  }
  let idT = 200;
  for (const bi of BIG_ISLES) {
    let bestDeep: { x: number; z: number; y: number } | null = null;
    for (let tries = 0; tries < 400; tries++) {
      const ang = rnd() * Math.PI * 2, rad = bi.R * 0.84 + rnd() * bi.R * 0.4;
      const x = bi.cx + Math.cos(ang) * rad, z = bi.cz + Math.sin(ang) * rad;
      const sea = groundAt(x, z);
      if (sea === -Infinity || sea > SEA_LEVEL - 85 || sea < SEA_LEVEL - 115) continue;
      if (crates.some((c) => Math.hypot(c.x - x, c.z - z) < 220)) continue;
      if (!bestDeep || sea < bestDeep.y) bestDeep = { x, z, y: sea };
    }
    if (!bestDeep) continue;
    const y = bestDeep.y + 6;
    const obj = spawnCrate(game.scene, bestDeep.x, y, bestDeep.z, { seed: idT + 1 });
    if (!obj) continue;
    obj.scale.set(1.7, 1.7, 1.7);
    const c: Crate = { id: idT, x: bestDeep.x, y, z: bestDeep.z, obj, loot: treasureLoot(rnd), opened: false, tier: 'treasure' };
    if (openedCrates.includes(idT)) { c.opened = true; obj.visible = false; }
    crates.push(c);
    idT++;
  }
  console.log('[game] big isle crates: %d', crates.filter((c) => c.id >= 100).length);
}

// --- 冒険の構造: 毎朝の漂着物 → 瓶の手紙 → 地図にない海域(船乗りの遺品) --------------
// 朝が来るたび浜に漂着物が流れ着く(=毎朝浜を見に行く日課のループ)。漂着物からたまに
// 瓶の手紙が出て、コンパスに❓の海域が現れる。潜ると豪華報酬+たまに次の手紙(発見の連鎖)。
// 手がかりの海域は浅い島棚から始まり、後の手紙ほど深い外洋(=サメの海)へ——難度が自然に上がる。
let flotsam: Crate | null = null;   // 今朝の漂着物(1日1個。翌朝、未開封でも入れ替わる=一期一会)
let relicSpots: { x: number; y: number; z: number }[] = []; // 手紙が示す海域の決定論候補(浅い順)
let relicNext = 0;                  // 何通目まで手紙を読んだか(PROG_KEYに保存。発動は常に候補順)

// 遺品スポットの決定論計算(placeCratesの後に一度)。島棚の浅場4 + 深い外洋4 → 浅い順。
function computeRelicSpots(): void {
  let s = 424243; const rnd = () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  const anchors: { cx: number; cz: number }[] = [];
  for (const o of ISLAND_OFFSETS) anchors.push({ cx: 500 + o.dx, cz: -500 + o.dz });
  for (const ci of customIslands) anchors.push({ cx: ci.cx, cz: ci.cz });
  const spots: { x: number; y: number; z: number }[] = [];
  const clear = (x: number, z: number) =>
    !crates.some((c) => Math.hypot(c.x - x, c.z - z) < 320) && !spots.some((q) => Math.hypot(q.x - x, q.z - z) < 500);
  for (const a of anchors) { // 島棚(見た目の棚の上、潜水が気持ちいい深さ)
    for (let tries = 0; tries < 300; tries++) {
      const ang = rnd() * Math.PI * 2, rad = 420 + rnd() * 700;
      const x = a.cx + Math.cos(ang) * rad, z = a.cz + Math.sin(ang) * rad;
      const sea = groundAt(x, z);
      if (sea === -Infinity || sea > SEA_LEVEL - 55 || sea < SEA_LEVEL - 110) continue;
      if (!clear(x, z)) continue;
      spots.push({ x, y: sea + 5, z });
      break;
    }
  }
  for (let tries = 0; tries < 800 && spots.length < anchors.length + 4; tries++) { // 深い外洋(サメの海)
    const x = (rnd() - 0.5) * 11000, z = (rnd() - 0.5) * 11000;
    if (anchors.some((a) => Math.hypot(x - a.cx, z - a.cz) < 1900)) continue;
    if (Math.hypot(x - 500, z - 3200) < 1100) continue; // 海溝の主の縄張りは避ける
    if (groundAt(x, z) !== -Infinity) continue;         // 真の外洋(裾野の見えない斜面には置かない)
    if (!clear(x, z)) continue;
    spots.push({ x, y: SEABED_MIN, z });                // 砂平面に半埋まり(主の財宝と同じ)
  }
  spots.sort((a, b) => b.y - a.y); // 浅い順=最初の手紙ほど易しい
  relicSpots = spots;
  console.log('[game] relic spots: %d (shallow %d / abyss %d)', spots.length, spots.filter((q) => q.y > -140).length, spots.filter((q) => q.y <= -140).length);
}

// 8方位名(北=-z)。手紙の文面用。
function compass8(dx: number, dz: number): string {
  const names = ['北', '北東', '東', '南東', '南', '南西', '西', '北西'];
  return names[((Math.round(Math.atan2(dx, -dz) / (Math.PI / 4)) % 8) + 8) % 8];
}

// 瓶の手紙を読む: 次の遺品スポットを世界にspawnし、コンパスに❓が現れる。候補が尽きたらfalse。
function triggerLetter(): boolean {
  if (relicNext >= relicSpots.length) return false;
  const idx = relicNext++;
  const sp = relicSpots[idx];
  const obj = spawnCrate(game.scene, sp.x, sp.y, sp.z, { seed: 800 + idx });
  if (obj) {
    obj.scale.set(1.4, 1.4, 1.4);
    crates.push({ id: 8000 + idx, x: sp.x, y: sp.y, z: sp.z, obj, loot: relicLoot(idx), opened: false, tier: 'relic' });
  }
  requestSave(); // relicNextはインベントリと対で保存(進行ロス防止の規律)
  const dir = compass8(sp.x - player.pos.x, sp.z - player.pos.z);
  const deep = sp.y <= -140;
  sfx('discover');
  hud.toast(`🍾 瓶の手紙: 『積み荷ごと沈んだ…${dir}の${deep ? '沖合、深い外洋だ。サメに気をつけろ' : '沖、島の棚の上だ'}』`, 'rare');
  award('first_letter');
  return true;
}

// 船乗りの遺品(手紙の報酬)。決定論で個体差、深い外洋(idx後半)ほど豪華。
function relicLoot(idx: number): { item: string; count: number }[] {
  let s = (idx + 1) * 7919; const rnd = () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  const deepBonus = relicSpots[idx] && relicSpots[idx].y <= -140 ? 1 : 0;
  return [
    { item: 'plank', count: 3 + deepBonus + Math.floor(rnd() * 2) },
    { item: 'rope', count: 2 + deepBonus },
    { item: 'fiber', count: 3 + Math.floor(rnd() * 2) },
    { item: 'fish', count: 1 + Math.floor(rnd() * 2) },
  ];
}

// ロード復元: 読んだ手紙ぶんの遺品スポットを再spawn(開封済みは畳む)。
function restoreRelics(): void {
  for (let i = 0; i < relicNext && i < relicSpots.length; i++) {
    if (crates.some((c) => c.id === 8000 + i)) continue;
    const sp = relicSpots[i];
    const obj = spawnCrate(game.scene, sp.x, sp.y, sp.z, { seed: 800 + i });
    if (!obj) continue;
    obj.scale.set(1.4, 1.4, 1.4);
    const c: Crate = { id: 8000 + i, x: sp.x, y: sp.y, z: sp.z, obj, loot: relicLoot(i), opened: false, tier: 'relic' };
    if (openedCrates.includes(c.id)) { c.opened = true; obj.visible = false; }
    crates.push(c);
  }
}

// 漂着物の中身: 日々の basics(素材/食料)をランダム2種。
function flotsamLoot(): { item: string; count: number }[] {
  const pool: { item: string; count: number }[] = [
    { item: 'wood', count: 2 + Math.floor(Math.random() * 2) },
    { item: 'fiber', count: 2 + Math.floor(Math.random() * 2) },
    { item: 'rope', count: 1 },
    { item: 'plank', count: 1 },
    { item: 'fish', count: 1 + Math.floor(Math.random() * 2) },
    { item: 'coconut', count: 2 },
  ];
  const a = Math.floor(Math.random() * pool.length);
  let b2 = Math.floor(Math.random() * (pool.length - 1));
  if (b2 >= a) b2++;
  return [pool[a], pool[b2]];
}

// 新しい朝: 浜の汀線に漂着物を流れ着かせる(古いものは未開封でも波が持ち去る)。
function spawnMorningFlotsam(): void {
  if (flotsam) {
    if (flotsam.obj) game.scene.remove(flotsam.obj);
    const i = crates.indexOf(flotsam);
    if (i >= 0) crates.splice(i, 1);
    flotsam = null;
  }
  if (!spawnBeach) return;
  const sb = spawnBeach;
  const tx = -sb.sz, tz = sb.sx; // 海方向(sx,sz)の直交=汀線方向
  for (let tries = 0; tries < 40; tries++) {
    const along = (Math.random() - 0.5) * 700; // 汀線沿いに散らす(毎朝違う場所)
    const x0 = sb.x + tx * along, z0 = sb.z + tz * along;
    for (let d = -40; d <= 200; d += 12) { // 海方向へ濡れた砂(汀線)を探す
      const x = x0 + sb.sx * d, z = z0 + sb.sz * d;
      const g = groundAt(x, z);
      if (g > 0.3 && g < 4) {
        const obj = spawnCrate(game.scene, x, g + 2, z, { seed: 40 + clock.day });
        if (!obj) return;
        obj.scale.set(0.8, 0.8, 0.8);
        flotsam = { id: 9000 + clock.day, x, y: g + 2, z, obj, loot: flotsamLoot(), opened: false, tier: 'flotsam' };
        crates.push(flotsam);
        hud.toast('🌊 夜のうちに何かが浜に流れ着いたようだ');
        return;
      }
    }
  }
}

// リーチ内の未開封クレート(3D最寄り)。潜水/遊泳中に届く。
function nearestCrate(): Crate | null {
  let best: Crate | null = null, bd = CRATE_REACH * CRATE_REACH;
  for (const c of crates) {
    if (c.opened) continue;
    const dx = c.x - player.pos.x, dy = c.y - player.pos.y, dz = c.z - player.pos.z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < bd) { bd = d2; best = c; }
  }
  return best;
}
// 開封の泡バースト: 消えるだけだった山場に一拍。単一グループ使い回し(同時開封は直近を優先)
let bubbles: any = null;
let bubbleT = -1; // -1=停止中
const BUBBLE_SEC = 1.7;
function burstBubbles(x: number, y: number, z: number): void {
  if (y >= -34) return; // 水中のみ(-34=波の谷-32より下)。浜や水面際で泡が空中に出るのを防ぐ
  if (!bubbles) bubbles = spawnBubbleBurst(game.scene);
  if (!bubbles) return;
  bubbles.position.set(x, y, z);
  bubbleT = 0;
  bubbles.visible = true;
}
function bubbleTick(dt: number): void {
  if (bubbleT < 0 || !bubbles) return;
  bubbleT += dt;
  if (bubbleT > BUBBLE_SEC) { bubbleT = -1; bubbles.visible = false; return; }
  const t = bubbleT;
  const cam = game.camera;
  for (const b of bubbles.children) {
    const u = b.userData;
    b.position.x = u.ox + Math.sin(t * 5 + u.ph) * 0.8; // 揺らぎながら
    b.position.z = u.oz + Math.cos(t * 4 + u.ph) * 0.8;
    b.position.y = 2 + u.vy * t;                        // 立ち上る
    if (cam) b.quaternion.copy(cam.quaternion);         // ビルボード=どの角度でも丸
    // 膨らみつつ、終盤0.45秒で縮んで弾ける(不透明材質のためopacityフェードは不可)
    const s = (0.8 + t * 0.5) * Math.min(1, (BUBBLE_SEC - t) / 0.45);
    b.scale.x = s; b.scale.y = s; b.scale.z = s;
  }
}

function openCrate(c: Crate): void {
  // 主(ヌシ)が守る財宝は、討伐するまで開けられない(縄張りの緊張感)
  if (c.id === boss.crateId && !boss.dead) {
    sfx('shark');
    hud.toast('🦈 巨大な影がこの財宝を守っている… 🔱銛で主を討て！');
    return;
  }
  c.opened = true;
  if (c.obj) c.obj.visible = false;
  burstBubbles(c.x, c.y, c.z); // 開けた場所から泡が立ち上る(水中のみ=burstBubbles内ガード)
  for (const g of c.loot) add(g.item, g.count);
  // 漂着物は揮発(翌朝入替・リロードで再抽選)なので開封idを永続させない
  if (c.tier !== 'flotsam') { openedCrates.push(c.id); requestSave(); } // 戦利品と対の保存(単独saveProgressは進行ロスの元)
  const spoils = c.loot.map((g) => `+${g.count} ${ITEMS[g.item]?.name ?? g.item}`).join('  ');
  hud.updateHotbar(); hud.refreshCraft();
  if (c.tier === 'flotsam') {
    sfx('pickup'); sfx('gather');
    hud.toast('🌊 漂着物を拾った  ' + spoils);
    // 瓶の手紙: 最初の漂着物には必ず(構造への導入)、以降は時々入っている
    if (relicNext === 0 || Math.random() < 0.25) triggerLetter();
  } else if (c.tier === 'relic') {
    gameStats.salvaged += 1;
    sfx('win'); sfx('pickup');
    hud.toast('🗺 手紙の示す場所に船乗りの遺品があった！  ' + spoils);
    award('first_salvage');
    award('relic_hunter');
    if (Math.random() < 0.3) triggerLetter(); // 遺品の中に濡れた手紙がもう一枚…(発見の連鎖)
  } else if (c.tier === 'treasure') {
    gameStats.salvaged += 1;
    sfx('win'); sfx('pickup');
    hud.toast('💎 深海の財宝を手に入れた！  ' + spoils);
    award('first_salvage');
    award('first_treasure');
  } else {
    gameStats.salvaged += 1;
    sfx('pickup'); sfx('gather');
    hud.toast('📦 沈没物資を回収した  ' + spoils);
    award('first_salvage');
  }
  const rare = c.loot.find((g) => RARE_USE[g.item]); // 財宝に眠っていた島の秘宝(add済み)は入手演出+入手歴
  if (rare) foundRare(rare.item);
  // 全回収の実績は「初期配置の沈没物資と財宝」が対象(毎朝湧く漂着物・手紙の遺品は含めない)
  if (crates.filter((k) => k.tier !== 'flotsam' && k.tier !== 'relic').every((k) => k.opened)) award('sea_sweeper');
}

// --- ボス「主(ヌシ)」: 最深の財宝を守る巨大ザメ --------------------------------------
// 縄張り(財宝の周囲)に入ると現れ、財宝を守って旋回する。銛5突きで討伐→財宝の封印が解け、
// 大量のサメ肉。ゲームのクライマックス=「深く潜る勇気」への最大の報酬。
const boss = {
  obj: null as any,
  crateId: -1,          // 守っている財宝のid(placeCratesが設定)
  active: false,        // プレイヤーが縄張り内
  angle: 0,
  timer: 0,             // 攻撃クールダウン
  hp: 170,              // 銛(34)で5突き
  dead: false,
  sinking: 0,           // 討伐演出の残り秒(仰向けで沈んでいく)
  glow: 0,              // 現在のemissiveIntensity(天候で切替。0=未設定)
};
const BOSS_HP = 170;
const BOSS_TERRITORY = 700;  // 縄張り半径(この距離で出現)
const BOSS_DMG = 24;         // 主の一撃は通常ザメより痛い

function bossCrate(): Crate | null { return crates.find((c) => c.id === boss.crateId) || null; }

// 縄張り旋回の目標点: 半径rを試し、海底が遊泳深度より浅い(=島の斜面/陸)なら財宝側へ縮めて
// 「必ず深い水中」の点を返す。財宝の直上(r=40)は配置条件により常に深いので必ず解がある。
// 深度は「プレイヤーの足元30下」に追従(下限=巣)。海面からも巨大な影が見え、潜れば常に足元に付いてくる。
// 30はorbitR最小60・噛みつき距離75との幾何(√(60²+30²)=67<75)で決めた=財宝直上でも噛める。
function bossOrbitPoint(c: Crate, angle: number, r: number): { x: number; y: number; z: number } {
  const swimY = Math.max(c.y + 30, Math.min(SEA_LEVEL - 12, player.pos.y - 30));
  for (let rr = r; rr >= 40; rr -= 40) {
    const x = c.x + Math.cos(angle) * rr, z = c.z + Math.sin(angle) * rr;
    const g = groundAt(x, z);
    if (g === -Infinity || g < swimY - 15) return { x, y: swimY, z }; // 体高ぶんの余裕がある深さ
  }
  return { x: c.x + Math.cos(angle) * 40, y: swimY, z: c.z + Math.sin(angle) * 40 };
}

function tickBoss(dt: number): void {
  if (titleMode) return; // 周回カメラが縄張り上空を通っても主を出現させない
  // 討伐演出: 仰向けにゆっくり沈んでいく(即消えの味気なさを解消)
  if (boss.sinking > 0 && boss.obj) {
    boss.sinking -= dt;
    boss.obj.position.y -= dt * 26;               // 沈む
    boss.obj.rotation.z += dt * (Math.PI / 3.2);  // 仰向けへ転がる
    if (boss.sinking <= 0) boss.obj.visible = false;
    return;
  }
  if (boss.dead || boss.crateId < 0 || !stats.alive || escaped) {
    boss.active = false;
    if (boss.obj) boss.obj.visible = false;
    return;
  }
  const c = bossCrate();
  if (!c) return;
  const dx = c.x - player.pos.x, dz = c.z - player.pos.z;
  const distToLair = Math.hypot(dx, dz);
  const inWater = player.mode === 'swim' || player.mode === 'dive';
  // 縄張りに入る(水中)と出現、離れると潜って消える
  if (!boss.active) {
    if (distToLair < BOSS_TERRITORY && inWater && !riding) {
      if (!boss.obj) {
        boss.obj = spawnShark(game.scene);
        if (boss.obj) {
          boss.obj.scale.set(2.6, 2.6, 2.6);
          // 主の見た目: 黒ずんだ体色+不気味な赤い底光り(通常ザメの明るい灰色と一目で区別できる)。
          // 水面用の白い引き波板(vertexColors無し)は深海では棒状の破綻に見えるため主には付けない。
          boss.obj.traverse((o: any) => {
            if (!o.isMesh || !o.material) return;
            if (!o.material.vertexColors) { o.visible = false; return; }
            if (o.material.color) o.material.color.setHex(0x3a3742);
            // emissiveは夜に唯一の光源になる: 強すぎると真っ赤な玩具になる(1.0で実測NG)。かすかな底光りに留める
            if (o.material.emissive) { o.material.emissive.setHex(0x330a10); o.material.emissiveIntensity = 0.5; }
            o.material.needsUpdate = true;
          });
        }
      }
      if (!boss.obj) return;
      boss.active = true; boss.timer = 2.5; boss.angle = Math.atan2(player.pos.z - c.z, player.pos.x - c.x);
      // 縄張り(財宝の周囲)に即出現=「深みから現れた」。原点から泳いで来る間抜けを防ぐ。
      // 出現点も深い場所を選ぶ(島の斜面へめり込んで見えない問題の対策)
      const sp0 = bossOrbitPoint(c, boss.angle, 200);
      boss.obj.position.set(sp0.x, sp0.y, sp0.z);
      boss.obj.visible = true;
      sfx('boss'); // 専用スティンガー(通常ザメのsharkと音で区別)
      hud.toast('🦈🦈 海溝の主だ！ 財宝を守っている——討つか、退くか');
    }
    return;
  }
  if (distToLair > BOSS_TERRITORY * 1.3 || (!inWater && distToLair > 300) || riding) {
    boss.active = false; if (boss.obj) boss.obj.visible = false; // 縄張りに戻る(HPは維持=削り戦法可)
    hud.toast('🦈 主は縄張りへ戻っていった');
    return;
  }
  // 財宝とプレイヤーの間を旋回しつつ、間合いを詰める
  boss.timer -= dt;
  boss.angle += dt * 0.7;
  const px = player.pos.x, pz = player.pos.z;
  const orbitR = Math.max(60, Math.min(240, Math.hypot(px - c.x, pz - c.z) * 0.55));
  // 目標点は「海底が十分深い」場所を選ぶ(島の斜面へめり込んで見えなくなる問題の対策)
  const tp = bossOrbitPoint(c, boss.angle, orbitR);
  // 現在位置から目標へなめらかに移動(巨体はゆっくり)
  const bx = boss.obj.position.x, bz = boss.obj.position.z;
  const mvx = tp.x - bx, mvz = tp.z - bz, mvl = Math.hypot(mvx, mvz) || 1;
  const sp = 150 * dt;
  const nx = bx + (mvx / mvl) * Math.min(sp, mvl), nz = bz + (mvz / mvl) * Math.min(sp, mvl);
  // 移動経路上でも地形の上へ押し出す(目標点間の斜面横断への保険)
  let ny = tp.y;
  const gHere = groundAt(nx, nz);
  if (gHere !== -Infinity && ny < gHere + 12) ny = Math.min(SEA_LEVEL - 8, gHere + 12);
  boss.obj.position.set(nx, ny, nz);
  boss.obj.rotation.y = Math.atan2(mvx, mvz) + Math.PI; // 進行方向へ向く
  // 嵐は水が濁って影が読めない: 底光りを大幅に強め「murkの中で動く赤い光」として読ませる。晴れ/夜は較正済みの0.5
  const glow = weather.raining ? 3.5 : 0.5;
  if (boss.glow !== glow) {
    boss.glow = glow;
    boss.obj.traverse((o: any) => { if (o.isMesh && o.material?.emissive) o.material.emissiveIntensity = glow; });
  }
  // プレイヤーが至近(潜って財宝に迫る)なら噛む。3D距離=真下の見えない主から噛まれる理不尽を防ぐ
  const pd = Math.hypot(px - boss.obj.position.x, player.pos.y - boss.obj.position.y, pz - boss.obj.position.z);
  if (pd < 75 && boss.timer <= 0 && inWater) {
    damage(BOSS_DMG);
    gameStats.bites += 1;
    lastBiteAt = performance.now(); lastBiteBoss = true;
    // hurt音は被ダメ監視(prevHealth)が鳴らす(ここでも鳴らすと1噛みで二重再生になる)
    hud.updateStats(stats);
    hud.toast('🦈🦈 主の一撃！ 距離を取れ！');
    boss.timer = 3.2;
  }
}

function hitBoss(): boolean {
  // 前方至近の主に銛が届けばヒット(attackから呼ばれる)
  if (!boss.active || boss.dead || !boss.obj) return false;
  const dx = boss.obj.position.x - player.pos.x, dy = boss.obj.position.y - player.pos.y, dz = boss.obj.position.z - player.pos.z;
  const dist = Math.hypot(dx, dy, dz);
  const hDist = Math.hypot(dx, dz);
  const fx = -Math.sin(player.yaw), fz = -Math.cos(player.yaw);
  const dot = (dx * fx + dz * fz) / (hDist || 1);
  if (dist >= SPEAR_REACH * 1.6 || dot <= 0.2) return false; // 巨体なので届く範囲は広め(3D距離)
  boss.hp -= spearDmg();
  boss.timer = Math.min(boss.timer, 0.9); // 突かれた主は怒って即反撃に来る(出現直後の空白2.5秒を連打で無傷討伐できてしまう対策。間合いを取れば従来通り安全)
  sfx('hurt');
  player._shake = 0.05;
  hitFlash(); // 命中の白フラッシュ(手応え)
  if (boss.hp <= 0) {
    boss.dead = true; boss.active = false;
    boss.sinking = 3.2; // 仰向けに沈む討伐演出(tickBossが処理)
    if (boss.obj) burstBubbles(boss.obj.position.x, boss.obj.position.y, boss.obj.position.z); // 討伐の泡柱
    add('shark_meat', 6);
    gameStats.sharksKilled += 1;
    bossKilledSaved = true; requestSave(); // 討伐も戦利品(サメ肉)と対で保存
    sfx('win');
    hud.updateHotbar(); hud.refreshCraft();
    hud.toast('👑 海溝の主を討ち取った！ 🥩サメ肉+6 — 財宝の封印が解けた');
    award('boss_slayer');
  } else {
    hud.toast(`🔱 主に銛が刺さった！ あと${Math.max(1, Math.ceil(boss.hp / spearDmg()))}突き`);
  }
  return true;
}
let bossKilledSaved = false; // PROG_KEYに保存する討伐フラグ

// --- サメ(危険要素) ------------------------------------------------------------
// 深い外洋を泳いでいると出現し、旋回しながら接近→攻撃。いかだ上・陸・浅瀬は安全。
const shark = {
  obj: null as any,
  state: 'idle' as 'idle' | 'stalk' | 'flee',
  timer: 0,          // idle: 出現までの蓄積秒 / stalk: 攻撃クールダウン
  angle: 0,          // 旋回角
  dist: 0,           // プレイヤーからの距離
  danger: 0,         // 深海滞在の蓄積(出現判定)
  hp: 0,             // サメの体力(銛で削る。0で討伐)
};
const SHARK_DEEP = -40;        // これより深い海底=外洋(棚の外)でのみ出現
const SHARK_SPAWN_SEC = 12;    // 外洋滞在この秒数で出現(昼・晴天基準。夜/嵐で短縮)
const SHARK_DMG = 16;
const SHARK_MAX_HP = 100;      // 銛(34dmg)で約3突き

// 脅威の段階化: 夜と嵐でサメが早く出現し・速く迫り・強く噛む。昼夜/天候を「駆け引き」にする。
function threatMul(): number {
  const tod = timeOfDay(), isNight = tod < 0.22 || tod >= 0.80;
  return 1 + (isNight ? 0.6 : 0) + (weather.raining ? 0.5 : 0); // 昼晴=1.0 / 夜=1.6 / 夜嵐=2.1
}

function playerInDeepWater(): boolean {
  if (riding) return false;
  if (player.mode !== 'swim' && player.mode !== 'dive') return false;
  // 全島の島棚(浅瀬)判定: どの島の縁からも遠い=海底が深い
  const seabed = groundAt(player.pos.x, player.pos.z);
  return seabed === -Infinity || seabed < SHARK_DEEP;
}

function tickShark(dt: number): void {
  // titleMode: 周回カメラがplayer.posを外洋(深海)に置くため、ガードしないとタイトル画面でサメが湧いて噛む
  if (!stats.alive || escaped || titleMode) return;
  const deep = playerInDeepWater();
  if (shark.state === 'idle') {
    // 主(ヌシ)と対峙中は通常ザメを湧かせない(2匹の見分けがつかず混乱するため)
    shark.danger = deep && !boss.active ? shark.danger + dt : Math.max(0, shark.danger - dt * 2);
    if (shark.danger > SHARK_SPAWN_SEC / threatMul()) { // 夜/嵐は早く出現
      if (!shark.obj) shark.obj = spawnShark(game.scene);
      if (!shark.obj) return;
      shark.state = 'stalk'; shark.timer = 4; shark.angle = Math.random() * Math.PI * 2; shark.dist = 260;
      shark.hp = SHARK_MAX_HP;
      shark.obj.visible = true;
      sfx('shark');
      hud.toast(hasSpear() ? '🦈 サメだ！ 🔱銛で反撃できる（クリック/突）' : '🦈 サメの気配がする…！ 陸かいかだへ逃げろ');
    }
    return;
  }
  if (shark.state === 'stalk') {
    // 安全圏に逃げたら退散。主(ヌシ)が現れた時も格上に道を譲って退散(乱入で死因が紛れるのを防ぐ)
    if (!deep || boss.active) { shark.state = 'flee'; shark.timer = 2.5; hud.toast('🦈 サメが離れていった'); return; }
    // 旋回しつつ距離を詰める(夜/嵐ほど速く迫る)
    const tm = threatMul();
    shark.angle += dt * 0.9;
    shark.dist = Math.max(24, shark.dist - dt * 46 * tm);
    shark.timer -= dt;
    const sx = player.pos.x + Math.cos(shark.angle) * shark.dist;
    const sz = player.pos.z + Math.sin(shark.angle) * shark.dist;
    shark.obj.position.set(sx, SEA_LEVEL - 6, sz);
    shark.obj.rotation.y = -shark.angle; // 旋回方向へ向く(接線方向)
    // 至近で攻撃(クールダウン付き。夜/嵐は噛む力も強い)
    if (shark.dist <= 30 && shark.timer <= 0) {
      damage(SHARK_DMG * (0.6 + 0.4 * tm));
      gameStats.bites += 1;
      lastBiteAt = performance.now(); lastBiteBoss = false;
      // hurt音は被ダメ監視(prevHealth)が鳴らす(ここでも鳴らすと1噛みで二重再生になる)
      hud.updateStats(stats);
      hud.toast('🦈 サメに噛まれた！ 陸かいかだへ逃げろ！');
      shark.timer = 3.5; shark.dist = 130; // 一撃後は距離を取って再接近
    }
    return;
  }
  // flee: 遠ざかって消える
  shark.timer -= dt;
  if (shark.obj) { shark.obj.position.x += dt * 220; shark.obj.position.z += dt * 160; }
  if (shark.timer <= 0) { if (shark.obj) shark.obj.visible = false; shark.state = 'idle'; shark.danger = 0; }
}

// --- 銛(スピア)でサメと戦う ------------------------------------------------------
// 銛を持っていると左クリック/「突」で突き攻撃。前方至近のサメにヒットしてHPを削り、
// 3突きで討伐→サメ肉(高栄養)を得る。「逃げるだけ」の脅威を「狩れる獲物」に変える。
let attackCooldown = 0;
const SPEAR_REACH = 96;  // 突きの届く距離(≈4.8m)
const SPEAR_DMG = 34;    // 1突きのダメージ
const OBSIDIAN_DMG = 55; // 黒曜石の銛(レア素材強化): 並のサメ2突き・主は4突き
function hasSpear(): boolean { return (inventory['spear'] || 0) > 0 || (inventory['obsidian_spear'] || 0) > 0; }
function spearDmg(): number { return (inventory['obsidian_spear'] || 0) > 0 ? OBSIDIAN_DMG : SPEAR_DMG; }

function attack(): void {
  if (!player.enabled || !stats.alive || player.frozen || titleMode) return;
  if (!hasSpear()) { hud.toast('🔱 銛が必要だ（Cでクラフト: 木2・石1・繊維2）'); return; }
  if (attackCooldown > 0) return;
  attackCooldown = 0.55;
  heldSwing = HELD_SWING_SEC; // 手元の銛が突き出るアニメ
  player._shake = 0.03;   // 突きの手応え(カメラがぶれる)
  sfx('gather');
  if (hitBoss()) return;  // 主(ヌシ)が至近なら優先して突く
  // 前方の至近にサメがいれば命中(視線方向との一致 + 距離)
  if (shark.state === 'stalk' && shark.obj) {
    const dx = shark.obj.position.x - player.pos.x, dz = shark.obj.position.z - player.pos.z;
    const dist = Math.hypot(dx, dz);
    const fx = -Math.sin(player.yaw), fz = -Math.cos(player.yaw);
    const dot = (dx * fx + dz * fz) / (dist || 1);
    if (dist < SPEAR_REACH && dot > 0.25) {
      shark.hp -= spearDmg();
      shark.dist = Math.min(220, shark.dist + 55); // 突かれて怯み距離を取る
      shark.timer = Math.max(shark.timer, 1.2);    // 攻撃を一瞬遅らせる
      sfx('hurt');
      player._shake = 0.045;
      hitFlash(); // 命中の白フラッシュ
      if (shark.hp <= 0) { killShark(); return; }
      const hits = Math.max(1, Math.ceil(shark.hp / spearDmg()));
      hud.toast(`🔱 銛が刺さった！ あと${hits}突き`);
      return;
    }
  }
  hud.toast('🔱 空振り…'); // 手応えは出すが外した
}

function killShark(): void {
  gameStats.sharksKilled += 1;
  const meat = 2 + ((gameStats.sharksKilled * 7) % 2); // 2〜3(決定論)
  add('shark_meat', meat);
  sfx('win');
  if (shark.obj) {
    burstBubbles(shark.obj.position.x, shark.obj.position.y, shark.obj.position.z); // 討伐の泡
    shark.obj.visible = false;
  }
  shark.state = 'idle'; shark.danger = -25; shark.hp = SHARK_MAX_HP; // 討伐後はしばらく安全
  hud.updateHotbar(); hud.refreshCraft();
  hud.toast(`🔱 サメを仕留めた！ 🥩 サメ肉 +${meat}`);
  award('shark_slayer');
}

// --- 一人称の手持ちアイテム(銛/斧): 「手触り」の核 -----------------------------------
// カメラロック方式のため、毎フレームworld空間で目線の右下へ配置する(ミラー無改変)。
// 出し分け: 銛>斧>非表示。突き/振り(swing)・歩行ボブ・着水で沈む揺れも演出。
let heldRoot: any = null;   // spawnHeldItemのGroup(children: held-spear / held-axe)
let heldSwing = 0;          // 突き/振りアニメの残り(0..HELD_SWING_SEC)
const HELD_SWING_SEC = 0.28;

let torchHeld = false; // 松明を手にしているか(夜の露出増感 applyExposureBoost が読む)
let fireRainMul = 1;   // 雨天の火勢(1=平常→0.6=雨)。焚き火と手持ち松明の炎が共有、約2.5秒でなめらかに遷移
function heldTick(now: number, dt: number): void {
  if (!heldRoot) return;
  if (heldSwing > 0) heldSwing = Math.max(0, heldSwing - dt);
  // 手持ち表示: 釣り中は竿(装備に関係なく)。次に装備品。未装備なら自動(夜=松明>銛>斧)。
  let held: string | null = null;
  const wet = player.mode === 'dive' || player.mode === 'swim'; // 遊泳中も炎が水面下に沈むため消灯
  const tod = timeOfDay(), isNight = tod < 0.22 || tod >= 0.80; // 昼の嵐(presetState='storm')では自動で取り出さない
  // 松明は意図的に無限持続(2026-07-08設計判断): 燃焼制は1晩252秒×最大7晩の本作では
  // 「毎晩3採取の作り直し」の純作業にしかならない(木/繊維は豊富で緊張を生まない)。夜の緊張はサメ強化と水中消灯が担う
  if (fishing > 0 || fishBite > 0) held = 'rod';
  else if (equipped === 'torch' || equipped === 'spear' || equipped === 'axe') held = equipped;
  else if (equipped === 'obsidian_spear') held = 'spear'; // 黒曜石の銛も銛メッシュ(見た目共通)
  else if (equipped === 'fishing_rod') held = 'rod';
  else if (!equipped || !EQUIPPABLE.has(equipped)) {
    held = ((inventory['torch'] || 0) > 0 && presetState !== 'day' && isNight && !wet) ? 'torch'
      : hasSpear() ? 'spear'
      : (inventory['axe'] || 0) > 0 ? 'axe' : null;
  }
  if (held === 'torch' && wet) held = null; // 手動装備でも水中では炎を出さない(上陸で再点灯)
  const wantTorch = held === 'torch';
  const wantSpear = held === 'spear';
  const wantAxe = held === 'axe';
  const wantRod = held === 'rod';
  const show = (wantTorch || wantSpear || wantAxe || wantRod) && player.enabled && !player.frozen && !titleMode && stats.alive && !escaped;
  torchHeld = wantTorch && show;
  heldRoot.visible = show;
  if (!show) return;
  for (const ch of heldRoot.children) {
    ch.visible = (ch.name === 'held-torch' && wantTorch) || (ch.name === 'held-spear' && wantSpear) || (ch.name === 'held-axe' && wantAxe) || (ch.name === 'held-rod' && wantRod);
    if (ch.name === 'held-torch' && wantTorch) {
      ch.traverse((f: any) => { // 炎の揺らめき(雨天は火勢が弱まる)
        if (f.name !== 'held-torch-flame') return;
        f.scale.y = (0.85 + 0.25 * Math.sin(now * 0.011 + f.rotation.y * 3)) * fireRainMul;
        f.rotation.y += dt * 1.6;
      });
    }
  }
  // 目線基準の座標系(YXZ): 前方f・右r・上u
  const cp = Math.cos(player.pitch), sp = Math.sin(player.pitch), sy = Math.sin(player.yaw), cy = Math.cos(player.yaw);
  const fx = -cp * sy, fy = sp, fz = -cp * cy;
  const rx = cy, rz = -sy;
  const ux = fy * -rz, uy = fx * rz - fz * rx, uz = fy * rx; // u = f×r ≈ 画面上方向(逆符号込み)
  const E = player.eyeHeight;
  // 突き: 前へ12、引き戻しはease。振り(斧/空振り)も同モーション。
  const t = heldSwing > 0 ? Math.sin((1 - heldSwing / HELD_SWING_SEC) * Math.PI) : 0;
  const thrust = t * E * 0.45;
  // 歩行/遊泳ボブ(移動中のみ)
  const moving = player.keys['KeyW'] || player.keys['KeyA'] || player.keys['KeyS'] || player.keys['KeyD'] || Math.abs(player.tmove.x) + Math.abs(player.tmove.y) > 0.1;
  const bob = moving ? Math.sin(now * 0.008) * E * 0.012 : 0;
  const ex = player.pos.x, ey = player.pos.y + E + player._bob, ez = player.pos.z;
  const R = E * 0.28, D = E * 0.30 - bob, F = E * 0.38 + thrust;
  heldRoot.position.x = ex + fx * F + rx * R - ux * D;
  heldRoot.position.y = ey + fy * F - uy * D + bob * 0.5;
  heldRoot.position.z = ez + fz * F + rz * R - uz * D;
  heldRoot.rotation.order = 'YXZ';
  heldRoot.rotation.y = player.yaw + 0.12;            // 内向き=穂先が照準へ寄る
  heldRoot.rotation.x = player.pitch + 0.10 + t * 0.2; // やや上向き(右下から中央へ構える)
  heldRoot.rotation.z = Math.sin(now * 0.0012) * 0.02; // 呼吸の微揺れ
}

// --- ボス戦のjuice: 主の体力バー + 命中の白フラッシュ(自前DOM=hud.tsに触れない) ----------
let bossBarEl: HTMLElement | null = null, bossBarFill: HTMLElement | null = null, bossBarDir: HTMLElement | null = null;
function bossBarTick(): void {
  const show = boss.active && !boss.dead && stats.alive && !titleMode;
  if (!bossBarEl) {
    if (!show) return; // 初出現まではDOMも作らない
    bossBarEl = document.createElement('div');
    bossBarEl.id = 'boss-bar';
    bossBarEl.style.cssText = 'position:fixed;left:50%;top:92px;transform:translateX(-50%);width:320px;z-index:9050;text-align:center;';
    const title = document.createElement('div');
    title.textContent = '👑 海溝の主';
    title.style.cssText = 'font-size:13px;color:#ffe9c9;text-shadow:0 1px 3px #000;margin-bottom:3px;letter-spacing:2px;';
    const frame = document.createElement('div');
    frame.style.cssText = 'height:12px;background:rgba(20,0,0,.55);border-radius:6px;overflow:hidden;box-shadow:0 0 0 1px rgba(255,200,140,.4) inset;';
    bossBarFill = document.createElement('div');
    bossBarFill.style.cssText = 'height:100%;width:100%;background:linear-gradient(90deg,#c62828,#ff8a65);transition:width .18s ease;';
    frame.appendChild(bossBarFill);
    bossBarDir = document.createElement('div');
    bossBarDir.id = 'boss-dir';
    bossBarDir.style.cssText = 'font-size:11px;color:#ffd7b0;text-shadow:0 1px 2px #000;margin-top:3px;letter-spacing:1px;';
    bossBarEl.appendChild(title); bossBarEl.appendChild(frame); bossBarEl.appendChild(bossBarDir);
    document.body.appendChild(bossBarEl);
  }
  bossBarEl.style.display = show ? 'block' : 'none';
  // toast(top:104)はバー(92〜約125)と重なるため、ボス戦中だけ下へ退避(hud.ts不触=並行セッション協調)
  const toastEl = document.getElementById('toast');
  if (toastEl) toastEl.style.top = show ? '150px' : '';
  if (show && bossBarFill) bossBarFill.style.width = Math.max(0, boss.hp / BOSS_HP * 100) + '%';
  // 方角矢印+距離: 嵐や夜は水中の主が見えない(フォグに勝てない)ため、UIで居場所のフェアネスを保証する
  if (show && bossBarDir && boss.obj) {
    const dx = boss.obj.position.x - player.pos.x, dz = boss.obj.position.z - player.pos.z;
    let rel = Math.atan2(-dx, -dz) - player.yaw;
    while (rel > Math.PI) rel -= 2 * Math.PI; while (rel < -Math.PI) rel += 2 * Math.PI;
    const arrows = ['↑', '↖', '←', '↙', '↓', '↘', '→', '↗'];
    const a = arrows[((Math.round(rel / (Math.PI / 4)) % 8) + 8) % 8];
    const dist = Math.round(Math.hypot(dx, boss.obj.position.y - player.pos.y, dz) / WORLD_PER_M);
    bossBarDir.textContent = `${a} ${dist}m`;
  }
}
// 命中の白フラッシュ(一瞬明滅→フェード)。CSSアニメで自己完結。
let flashEl: HTMLElement | null = null;
function hitFlash(): void {
  if (!flashEl) {
    flashEl = document.createElement('div');
    flashEl.style.cssText = 'position:fixed;inset:0;background:rgba(255,255,255,.28);pointer-events:none;z-index:9040;opacity:0;transition:opacity .3s ease;';
    document.body.appendChild(flashEl);
  }
  flashEl.style.transition = 'none'; flashEl.style.opacity = '1';
  requestAnimationFrame(() => { if (flashEl) { flashEl.style.transition = 'opacity .3s ease'; flashEl.style.opacity = '0'; } });
}

// --- 酸素バー(潜水中のみ画面中央下に表示。自前DOM=hud.tsに触れない) --------------------
let o2El: HTMLElement | null = null, o2Fill: HTMLElement | null = null;
let o2WarnAt = 0; // 溺れ警告トーストのスロットル
let o2LowWarned = false; // 残量25%の事前警告(1潜水1回。50%まで回復でリセット)
function o2Tick(diving: boolean): void {
  if (!o2El) {
    o2El = document.createElement('div');
    o2El.id = 'o2-bar';
    // タッチ端末は中央下だと「使う」ボタンと重なる(縦持ちで実測)ため、左寄せ+ステータスの真上に置く
    const pos = IS_TOUCH
      ? 'left:calc(12px + env(safe-area-inset-left, 0px));bottom:calc(150px + env(safe-area-inset-bottom, 0px));width:200px;'
      : 'left:50%;bottom:118px;transform:translateX(-50%);width:240px;';
    o2El.style.cssText = `position:fixed;${pos}height:14px;`
      + 'background:rgba(0,10,20,.55);border-radius:7px;overflow:hidden;z-index:9050;display:none;'
      + 'box-shadow:0 0 0 1px rgba(255,255,255,.25) inset;';
    o2Fill = document.createElement('div');
    o2Fill.style.cssText = 'height:100%;width:100%;background:linear-gradient(90deg,#3ec6ff,#aee9ff);transition:width .15s linear;';
    const label = document.createElement('span');
    label.textContent = 'O₂';
    label.style.cssText = 'position:absolute;left:8px;top:0;font-size:10px;line-height:14px;color:#eaf6ff;text-shadow:0 1px 1px #000;';
    o2El.appendChild(o2Fill); o2El.appendChild(label);
    document.body.appendChild(o2El);
  }
  const show = diving || stats.oxygen < stats.maxOxygen * 0.999; // 浮上直後も回復が見える
  o2El.style.display = show && stats.alive && !titleMode ? 'block' : 'none';
  if (!show || !o2Fill) return;
  const ratio = stats.oxygen / stats.maxOxygen;
  o2Fill.style.width = (ratio * 100) + '%';
  o2Fill.style.background = ratio < 0.25
    ? 'linear-gradient(90deg,#ff5252,#ffb199)'  // 残りわずか=赤(溺れ警告)
    : 'linear-gradient(90deg,#3ec6ff,#aee9ff)';
  // 事前警告: 赤バー(25%)到達時に一度だけ。酸素0で初めて知らせるのでは手遅れだった
  if (diving && ratio < 0.25 && !o2LowWarned) {
    o2LowWarned = true;
    hud.toast(IS_TOUCH ? '🫧 酸素が残りわずか…「浮上」で上がろう' : '🫧 酸素が残りわずか… そろそろ浮上を（Space）');
  }
  if (ratio > 0.5) o2LowWarned = false;
  if (diving && stats.oxygen <= 0 && performance.now() > o2WarnAt) {
    o2WarnAt = performance.now() + 2500;
    sfx('hurt'); // 溺れダメージ中の聴覚キュー(視界がトーストに向いていなくても伝わる)
    hud.toast(IS_TOUCH ? '🫧 息が続かない！「浮上」で上がれ！' : '🫧 息が続かない！ 浮上しろ！（Space）');
  }
}

// --- 移動音 --------------------------------------------------------------------
// 進んだ距離(歩幅)で足音(陸)/水かき(遊泳)を鳴らす。距離基準なので歩き/走りでテンポが自然に変わる。
let strideAcc = 0;
let stridePrev: { x: number; z: number } | null = null;
let splashMuteUntil = 0; // 入水音の連発防止(波打ち際のモード往復)
let fallSince = 0;       // fall開始時刻(着地ドスンの滞空時間ゲート)
function stepSfxTick(sprinting: boolean): void {
  const moveMode = player.mode === 'land' || player.mode === 'swim' || player.mode === 'dive';
  if (titleMode || !stats.alive || player.frozen || !moveMode) {
    stridePrev = null;
    if (player.mode !== 'fall') strideAcc = 0; // 岩場の小ジャンプ(一瞬fall)で歩調が消えないよう空中は蓄積保持
    return;
  }
  if (!stridePrev) { stridePrev = { x: player.pos.x, z: player.pos.z }; return; }
  const d = Math.hypot(player.pos.x - stridePrev.x, player.pos.z - stridePrev.z);
  stridePrev.x = player.pos.x; stridePrev.z = player.pos.z;
  if (d > 200) { strideAcc = 0; return; } // テレポートは鳴らさない
  strideAcc += d;
  // 歩き=約2.6歩/s、走り=歩幅を広げて約3.7歩/s(WALK=640world/s基準)。泳ぎはストローク周期を長く。
  const stride = (player.mode === 'land' ? 250 : 500) * (sprinting ? 1.8 : 1);
  if (strideAcc >= stride) { strideAcc -= stride; sfx(player.mode === 'land' ? 'step' : 'swim'); }
}

// --- 天候(雨) -----------------------------------------------------------------
// 時々スコールが来る。雨の間: 給水器が速く貯まる / 喉の渇きが少し癒える。
const weather = { raining: false, timer: 90 + Math.random() * 120, kind: 'storm' as 'storm' | 'foggy' }; // 次の切替までの秒 / 雨の種類
const RAIN_STILL_MUL = 6;   // 雨中の給水器の貯水倍率
const RAIN_THIRST = 0.45;   // 雨中の渇き回復/秒

function tickWeather(dt: number): void {
  if ((window as any).__dbgWeatherLock) return; // デバッグ: 天気を手動固定中は自動切替を止める
  weather.timer -= dt;
  if (weather.timer <= 0) {
    weather.raining = !weather.raining;
    if (weather.raining) { weather.timer = 50 + Math.random() * 70; weather.kind = Math.random() < 0.5 ? 'storm' : 'foggy'; hud.toast(weather.kind === 'storm' ? '🌧 スコールが来た！（給水のチャンス）' : '🌫 霧雨が立ちこめた（給水のチャンス）'); }
    else { weather.timer = 150 + Math.random() * 240; hud.toast('☀️ 雨が上がった'); }
    hud.setRain(weather.raining);
  }
  if (weather.raining && stats.alive) {
    stats.thirst = Math.min(stats.maxThirst, stats.thirst + RAIN_THIRST * dt);
  }
  lightningTick(dt);
}

// --- 稲妻(嵐のみ): 全画面の閃光(自前DOM)+距離感のある遅れで雷鳴。DOM+WebAudioのみ=描画層不干渉 ---
let lightningEl: HTMLElement | null = null;
let lightningTimer = 6 + Math.random() * 8; // 次の稲妻までの秒(嵐中のみ減る)
function lightningTick(dt: number): void {
  // frozen中は発火しない(テスト/スクショの決定性を守る。通常プレイでfrozenは演出中のみ)
  if (!weather.raining || weather.kind !== 'storm' || titleMode || !stats.alive || player.frozen) return;
  lightningTimer -= dt;
  if (lightningTimer > 0) return;
  lightningTimer = 7 + Math.random() * 11;
  if (!lightningEl) {
    lightningEl = document.createElement('div');
    lightningEl.id = 'lightning';
    lightningEl.style.cssText = 'position:fixed;inset:0;background:#eaf2ff;pointer-events:none;z-index:9035;opacity:0;transition:opacity .28s ease;';
    document.body.appendChild(lightningEl);
  }
  const el = lightningEl;
  el.style.transition = 'none'; el.style.opacity = '0.5';                                          // 一閃
  window.setTimeout(() => { el.style.opacity = '0.12'; }, 60);                                     // 二段目(明滅)
  window.setTimeout(() => { el.style.transition = 'opacity .28s ease'; el.style.opacity = '0'; }, 130);
  window.setTimeout(() => sfx('thunder'), 400 + Math.random() * 1200);                             // 遠雷の遅れ
}

// --- 釣り ---------------------------------------------------------------------
// 釣り竿を持ち、いかだの上か水辺で Q。数秒待つと「アタリ」が来て、E で合わせると釣れる
// (反応ミニゲーム。放置すると逃げる)。サメがいる間は釣れない。
let fishing = 0;  // 残り秒(0=していない)
let fishBite = 0; // アタリの残り秒(この間に E で合わせると釣れる)
const BITE_WINDOW = 1.4;
function canFish(): boolean {
  if ((inventory['fishing_rod'] || 0) <= 0) return false;
  if (riding) return true;
  const seabed = groundAt(player.pos.x, player.pos.z);
  return player.mode === 'swim' || (player.mode === 'land' && seabed < SEA_LEVEL + 8); // 波打ち際もOK
}
function startFishing(): void {
  if (fishing > 0) { fishing = 0; hud.toast('🎣 釣りをやめた'); return; }
  if (!canFish()) { hud.toast('🎣 釣り竿を持って水辺かいかだの上で'); return; }
  fishing = 3 + Math.random() * 3;
  sfx('cast'); // 竿の振り+着水(無音のキャストは手応えがなかった)
  hud.toast('🎣 釣り糸を垂らした…（動くと中断）');
}
function tickFishing(dt: number): void {
  // アタリ中: 時間切れで逃げられる
  if (fishBite > 0) {
    fishBite -= dt;
    if (fishBite <= 0) { hud.toast('…逃げられた'); }
    return;
  }
  if (fishing <= 0) return;
  // 移動・遊泳で中断(タッチジョイスティックも移動入力: モバイルだけ動いても中断しない非対称を防ぐ)
  const moving = player.keys['KeyW'] || player.keys['KeyA'] || player.keys['KeyS'] || player.keys['KeyD'] || Math.abs(player.tmove.x) + Math.abs(player.tmove.y) > 0.1;
  if (moving || !canFish()) { fishing = 0; return; }
  if (shark.state === 'stalk') { fishing = 0; hud.toast('🦈 サメがいて魚が寄らない…'); return; }
  fishing -= dt;
  if (fishing <= 0) {
    fishBite = BITE_WINDOW; // アタリ！ E で合わせる(反応ミニゲーム)
    sfx('fish');
    player._shake = 0.03; // 竿がしなる手応え
    hud.toast(IS_TOUCH ? '❗ アタリだ！ 今だ、「調べる」で合わせろ！' : '❗ アタリだ！ 今だ、E で合わせろ！');
  }
}
// アタリに合わせる(interactの最優先)。成功で魚、たまに大物(魚2)。
function hookFish(): void {
  fishBite = 0;
  const big = Math.random() < 0.25;
  add('fish', big ? 2 : 1);
  gameStats.fish += 1;
  sfx('fish'); sfx('pickup');
  heldSwing = HELD_SWING_SEC;
  hud.updateHotbar();
  hud.toast(big ? '🐟🐟 大物だ！ 魚 +2' : '🐟 魚が釣れた！');
  award('first_fish');
}

// --- 釣りウキ: 拝借ブイ(いかだと同方式)を釣り中だけ波高計にして、本物の波にウキを浮かべる。
// 終了パスが多い(やめる/移動/サメ/逃げられ/釣果)ため、毎フレームの状態監視で貸借を一元管理する。
let floatMesh: any = null;
let floatProbe: any = null;                  // 拝借中の景観ブイ(終了で元位置へ返却)
let floatHome: { x: number; z: number } | null = null;
let castPt: { x: number; z: number } | null = null;
let lineMesh: any = null;                    // 竿先→ウキの釣り糸
let lineTip: any = null;                     // 竿先ワールド座標の受け皿(Vector3使い回し)
function fishFloatTick(now: number): void {
  const active = (fishing > 0 || fishBite > 0) && stats.alive && !titleMode && !escaped;
  if (!active) {
    if (floatProbe) {
      floatProbe.position.x = floatHome!.x; floatProbe.position.z = floatHome!.z;
      floatProbe.visible = true; floatProbe.__gameProbe = false;
      floatProbe = null; floatHome = null;
    }
    castPt = null;
    if (floatMesh) floatMesh.visible = false;
    if (lineMesh) lineMesh.visible = false;
    return;
  }
  if (!floatMesh) floatMesh = spawnFishFloat(game.scene);
  if (!floatMesh) return;
  if (!floatProbe) {
    floatProbe = acquireWaveProbe();
    if (floatProbe) floatHome = { x: floatProbe.position.x, z: floatProbe.position.z };
  }
  if (!castPt) { // 投点: 視線方向で最初に水深が足りる距離。陸向きならウキは出さない(釣り自体は従来通り)
    // 水深は名目SEA_LEVELでなく実波面(ブイ推定)基準=浜の乾いた砂へ投げない
    const surf0 = floatProbe ? floatProbe.position.y + 4.25 : SEA_LEVEL;
    const sy = Math.sin(player.yaw), cy = Math.cos(player.yaw);
    for (const d of [90, 70, 50, 34]) {
      const x = player.pos.x - sy * d, z = player.pos.z - cy * d;
      if (groundAt(x, z) < surf0 - 6) { castPt = { x, z }; break; }
    }
    if (!castPt) return;
  }
  if (floatProbe) { floatProbe.position.x = castPt.x; floatProbe.position.z = castPt.z; }
  // 水面 = ブイy + 喫水バイアス4.25。胴の半分(1.7)を沈め、アタリでさらに沈めて「今引け」を見せる
  const surf = (floatProbe ? floatProbe.position.y + 4.25 : SEA_LEVEL) - 1.7;
  const dip = fishBite > 0 ? 3.2 : 0;
  floatMesh.position.x = castPt.x;
  floatMesh.position.y = surf - dip + Math.sin(now * 0.004) * 0.35;
  floatMesh.position.z = castPt.z;
  floatMesh.visible = true;
  // 釣り糸: 竿先(rod-tip)からウキの頭へ張る。heldTickが先に竿姿勢を書いた後に呼ばれる前提
  if (!lineMesh) lineMesh = spawnFishLine(game.scene);
  if (lineMesh && heldRoot && heldRoot.visible) {
    if (!lineTip) {
      let t: any = null;
      heldRoot.traverse((o: any) => { if (!t && o.name === 'rod-tip') t = o; });
      lineTip = t ? { mark: t, v: heldRoot.position.clone() } : null;
    }
    if (lineTip) {
      lineTip.mark.getWorldPosition(lineTip.v);
      lineMesh.position.copy(lineTip.v);
      lineMesh.lookAt(floatMesh.position.x, floatMesh.position.y + 4.9, floatMesh.position.z);
      const dxl = floatMesh.position.x - lineTip.v.x, dyl = floatMesh.position.y + 4.9 - lineTip.v.y, dzl = floatMesh.position.z - lineTip.v.z;
      lineMesh.scale.z = Math.max(1, Math.sqrt(dxl * dxl + dyl * dyl + dzl * dzl));
      lineMesh.visible = true;
    }
  } else if (lineMesh) lineMesh.visible = false;
}

// --- 装備システム: ホットバーで選んだ道具が「使う」の意味を決める(道具が増えてもボタンは増えない) ---
const EQUIPPABLE = new Set(['spear', 'obsidian_spear', 'axe', 'stone_knife', 'torch', 'fishing_rod', 'raft_floor', 'coconut', 'fish', 'shark_meat', 'cooked_fish', 'cooked_meat', 'flower_tea']);
let equipped: string | null = null;

function equip(item: string | null): void {
  if (item && (!EQUIPPABLE.has(item) || (inventory[item] || 0) <= 0)) return;
  equipped = equipped === item ? null : item; // 同じものを選ぶと外す
  hud.setEquipped(equipped);
  hud.updateHotbar();
  if (equipped) hud.toast(`${ITEMS[equipped]?.icon ?? ''} ${ITEMS[equipped]?.name ?? equipped} を持った`);
}

// 「使う」= 装備品のアクション。素手なら文脈インタラクト(E相当)。
function useEquipped(): void {
  if (!player.enabled || !stats.alive || player.frozen || titleMode) return;
  if (equipped && (inventory[equipped] || 0) <= 0) equipped = null; // 使い切りの後始末
  switch (equipped) {
    case 'spear': case 'obsidian_spear': attack(); return;
    case 'fishing_rod': startFishing(); return;
    case 'raft_floor': placeRaft(); return;
    case 'coconut': case 'fish': case 'shark_meat': case 'cooked_fish': case 'cooked_meat': case 'flower_tea':
      if (consume(equipped)) { sfx('eat'); hud.updateHotbar(); if ((inventory[equipped] || 0) <= 0) equipped = null; }
      return;
    case 'axe': case 'stone_knife': case 'torch': default:
      interact(); return; // 道具はパッシブ効果。素手/その他は文脈アクション
  }
}
(window as any).__equip = (i: string | null) => equip(i);
(window as any).__equipped = () => equipped;
(window as any).__useEquipped = () => useEquipped();

// E キー/採取ボタンの統一インタラクション: アタリ合わせ > 降りる > 乗る > 船修理 > 設置物 > 採取
function interact(): void {
  if (fishBite > 0) { hookFish(); return; } // 釣りのアタリが最優先(1.4秒の反応勝負)
  if (riding) { dismount(); return; }
  if (tryBoard()) return;
  if (nearShip() && repairRemaining() > 0) { doRepair(); return; }
  const p = nearestPlaced();
  if (p) {
    if (p.type === 'campfire') restAtFire();
    else if (p.type === 'shelter') sleepToMorning('shelter');
    else if (p.type === 'water_still') drinkFromStill(p);
    return;
  }
  const c = nearestCrate();
  if (c) { openCrate(c); return; }
  doGather();
}

// --- 移動・物理 -------------------------------------------------------------
let last = performance.now();
let lastSave = performance.now();
// 進捗イベント(クレート開封/主討伐)は必ずインベントリ(saveState)と対で保存する。
// saveProgress単独の先行保存は「リロードで開封済みなのに戦利品なし」の進行ロスになる
// (自動セーブは潜水中を除外するため)。次の安全なtick=浮上直後に自動セーブを即発火させる。
function requestSave(): void { lastSave = -1e9; }
let announcedDay = 0; // Day告知済みの日(0=未同期。開始/続きから直後は無音で同期)
let nextGullAt = 0; // 次のカモメの鳴き時刻(昼の浜の気配)
let deadHandled = false;
let prevHealth = 100; // 被ダメ演出用(前フレームの体力)
let lastHeartbeat = 0; // 低体力心音の最終再生時刻(ms)
let hintedFood = false, hintedWater = false; // 危機前ヒント(各1回/1プレイ。restartはreloadなので自然リセット)
let prevMode = 'swim'; // 浮上検知用(前フレームのmode)
let lastBiteAt = -1e9; // 死因判定: 直近のサメ被弾時刻
let lastBiteBoss = false; // 直近の噛みつきが海溝の主か(死因の出し分け)
let lastFallAt = -1e9;  // 死因判定: 直近の落下ダメージ時刻
let fallImpactVy = 0;   // 落下中の最大下降速度(着地ダメージ用。着地スナップでvyが消える前に捕捉)
let escapedShip: any = null; // 勝利演出: 出航していく難破船
let titleMode = false; // タイトル画面中(カメラが島を周回・入力/物理停止)
let titleAngle = 2.2;  // 周回カメラの角度

// --- プレイ統計と段階ガイド(オンボーディング) ---------------------------------
const gameStats = { gathered: 0, crafted: 0, fish: 0, sailed: 0, bites: 0, salvaged: 0, sharksKilled: 0 }; // sailed=航行距離 / salvaged=回収クレート / sharksKilled=サメ討伐
let guideStep = 0; // 0=採取 1=道具クラフト 2=いかだで島渡り 3=船修理(最終目標)
function tickGuide(): void {
  if (guideStep === 0 && gameStats.gathered > 0) { guideStep = 1; hud.toast(IS_TOUCH ? '👍 いいぞ！次は「作」ボタンでクラフトだ' : '👍 いいぞ！次は C でクラフトだ'); }
  else if (guideStep === 1 && (inventory['axe'] || 0) > 0) { guideStep = 2; hud.toast('🪓 斧を作った！ 木材の採取量が増える'); }
  else if (guideStep === 2 && riding) { guideStep = 3; hud.toast('⛵ その調子！ コンパスを頼りに島々を巡ろう'); }
}
function statsText(): string {
  // 各項目をnowrapで包み、折り返しが項目の切れ目でだけ起きるように(「サルベ/ージ」分断防止)
  const items = [
    `採取 ${gameStats.gathered}`, `クラフト ${gameStats.crafted}`, `釣り ${gameStats.fish}`,
    `サルベージ ${gameStats.salvaged}`, `サメ討伐 ${gameStats.sharksKilled}`,
    `航海 ${Math.round(gameStats.sailed / WORLD_PER_M)}m`, `サメ被弾 ${gameStats.bites}`,
    `🏆実績 ${unlockedCount()}/${FEATS.length}`,
  ];
  return items.map((s) => `<span style="white-space:nowrap">${s}</span>`).join(' ・ ');
}
// クリア評価: 脱出までの日数で S/A/B/C (リプレイ動機=次はもっと速く)
function escapeRank(day: number): string {
  return day <= 2 ? '🏆S' : day <= 4 ? '🥇A' : day <= 7 ? '🥈B' : '🥉C';
}
const PROG_KEY = 'side6-progress';
function saveProgress(): void { try { localStorage.setItem(PROG_KEY, JSON.stringify({ guideStep, gameStats, openedCrates, bossKilled: bossKilledSaved, visited: visitedIslands, rares: rareFound, relicNext })); } catch { /* ignore */ } }
function loadProgress(): void {
  try {
    const d = JSON.parse(localStorage.getItem(PROG_KEY) || 'null');
    if (d && typeof d.guideStep === 'number') {
      guideStep = d.guideStep; Object.assign(gameStats, d.gameStats || {});
      if (Array.isArray(d.openedCrates)) openedCrates = d.openedCrates;
      if (d.bossKilled) { bossKilledSaved = true; boss.dead = true; } // 主の討伐は再戦なし(縄張り解放を維持)
      if (Array.isArray(d.visited)) visitedIslands = d.visited.filter((v: unknown) => typeof v === 'string');
      if (Array.isArray(d.rares)) rareFound = d.rares.filter((v: unknown) => typeof v === 'string');
      if (typeof d.relicNext === 'number' && Number.isFinite(d.relicNext)) relicNext = Math.max(0, Math.floor(d.relicNext));
    }
  } catch { /* ignore */ }
}
function guideText(): string {
  switch (guideStep) {
    case 0: return IS_TOUCH ? '🎯 まずは素材集め — 「調べる」で椰子・草・岩から採取しよう' : '🎯 まずは素材集め — E で椰子・草・岩から採取しよう';
    case 1: return IS_TOUCH ? '🎯 「作」でクラフト — 繊維2→ロープ → 石斧を作ろう(木材+1)' : '🎯 C でクラフト — 繊維2→ロープ → 石斧を作ろう(木材+1)';
    case 2: return IS_TOUCH ? '🎯 いかだに近づいて「調べる」で乗り、左スティックで別の島へ渡ろう' : '🎯 いかだに近づいて E で乗り、W で別の島へ渡ろう';
    default: return `🎯 難破船を修理して脱出 ${repairLabel()} — 📦沈没物資と💎財宝が近道(🔱銛でサメと戦える)`;
  }
}

// --- 本物の夜: ミラーの天気プリセット(moonlit=星空)をゲーム内時刻で切り替える -------------
// 切替は内部で海/空の再構築が走り約10秒かかる(描画は継続)ので、日没・夜明けの少し前から始める。
let presetState: 'day' | 'night' | 'storm' = 'day';
let appliedPreset: string | null = null; // 現在ミラーに当たっているプリセット名(冗長切替の抑止)
let presetPollAt = 0;
// 晴天時の時刻→環境プリセット(夜→朝焼け→昼→夕)。プリセット切替はミラー側でHDRI天空の再デコードと
// PMREM環境マップ再生成(約0.5秒の凍結)を毎回伴い、キャッシュも無く不可避に重い。よって時間帯は4区分に
// 絞ってカクつきの回数を最小化する(arctic/blackFlagは近似色のため不採用。デバッグメニューからは選べる)。
function clearWeatherPreset(tod: number): string {
  if (tod >= 0.80 || tod < 0.20) return 'moonlit';   // 夜: 星空
  if (tod < 0.32) return 'dusk';                      // 朝: 朝焼け
  if (tod < 0.68) return 'seaOfThieves';              // 昼: 澄んだ碧
  return 'sunset';                                    // 夕: 暖色
}
// 現時刻+天気で当てるべきプリセット(夜は星空優先 > 雨は嵐/霧雨 > 晴天は時刻別)
function scheduledPreset(): string {
  const tod = timeOfDay();
  // 夜スコール: moonlitのままだと満天の星空に雨が降る矛盾になるため、雨天の夜は嵐(暗い雲天)にする。
  // 夜のDOM色調(setDayTintのNIGHT)がstormの雲天に重なり「暗い嵐の夜」として昼の嵐と区別される。
  if (tod >= 0.80 || tod < 0.20) return weather.raining ? 'storm' : 'moonlit';
  return weather.raining ? weather.kind : clearWeatherPreset(tod);
}
function findPresetSelect(): any {
  for (const el of Array.from(document.querySelectorAll('select'))) {
    const vals = Array.from((el as HTMLSelectElement).options).map((o) => o.value);
    if (vals.includes('moonlit')) return el;
  }
  return null;
}
function applyPreset(name: string): void {
  const sel = findPresetSelect();
  if (!sel || sel.value === name) return;
  sel.value = name;
  sel.dispatchEvent(new Event('input', { bubbles: true }));
  sel.dispatchEvent(new Event('change', { bubbles: true }));
}
(window as any).__applyPreset = applyPreset; // デバッグメニューからプリセット適用

// 潜水時/松明の視界確保: renderer.toneMappingExposure を増感する。
// 増感なしの間はバンドル既定の露出値を毎フレーム観測し、増感中は base * boost を毎フレーム設定(プリセット切替の上書きに勝つ)。
// 松明はCSS brightnessでは黒が持ち上がらず「全然明るくならない」(実測: 地面輝度25→33)ため、
// 真の露出×1.55で照らす(実測: 25→47)。水中(遊泳・潜水)は松明が消える(torchHeld=false)ので潜水増感とは実質排他。
let _baseExposure = 1;      // 増感なしの時に観測したバンドル既定露出
let _boostActive = false;   // いま増感を当てているか
const TORCH_EXPO = 1.55;    // 松明の火明かり(手持ち・非昼)
function applyExposureBoost(depth: number, torch: boolean): void {
  const r = (window as any).__game?.renderer;
  if (!r) return;
  const dbg = (window as any).__dbgUwExposure; // デバッグ手動上書き(水中ブースト)
  const torchMul = torch ? TORCH_EXPO : 1;
  if (depth < 0 && torchMul === 1) {
    // 増感なし: 当てていたら base を復元。当てていなければ現在値を base として観測。
    if (_boostActive) { r.toneMappingExposure = _baseExposure; _boostActive = false; }
    else _baseExposure = r.toneMappingExposure;
    return;
  }
  if (!_boostActive) { _baseExposure = r.toneMappingExposure; _boostActive = true; }
  // 水中: 浅い(depth≈0)で約1.7倍、深い(depth≥90world)で約3.0倍。実測: 露出3.5で mean17→61。
  const boost = depth < 0 ? 1 : (dbg != null ? dbg : 1.7 + Math.min(1.3, (depth / 90) * 1.3));
  r.toneMappingExposure = _baseExposure * boost * torchMul;
}
// 切替は約0.5秒の凍結を伴うため、水中(短時間)やボス戦中は先送りし、穏やかな瞬間にカクつきを逃がす。
let pendingPreset: string | null = null; // 適用したいが安全窓待ちのプリセット
let presetDeferSince = 0;                // 先送りを始めた時刻(ms)
function presetSafeWindow(): boolean {
  if ((window as any).__dbgPresetUnsafe) return false; // デバッグ: 先送りの検証用に安全窓を閉じる
  if (player.mode === 'dive') return false;    // 水中=浮上まで待つ
  if (boss.active && !boss.dead) return false; // ボス戦=決着まで待つ
  return true;
}
function prewarmPreset(): void {
  // 起動時プリロード: 時刻相当のプリセットを先に当て、初回の再生成コストをローディング/タイトル中に隠す。
  appliedPreset = scheduledPreset();
  applyPreset(appliedPreset);
}
function tickDayNightPreset(now: number): void {
  if ((window as any).__dbgPresetLock) return; // デバッグ: プリセット手動固定中は自動切替を止める
  if (now < presetPollAt) return;
  presetPollAt = now + 1000; // 1秒に1回で十分
  // 露出/松明/色調の粗い区分は「実際に当たっているプリセット」に追従(先送り中は見た目とズレない)。
  presetState = appliedPreset === 'moonlit' ? 'night'
    : (appliedPreset === 'storm' || appliedPreset === 'foggy') ? 'storm' : 'day';
  const target = scheduledPreset();
  if (target === appliedPreset) { pendingPreset = null; return; }
  if (target !== pendingPreset) { pendingPreset = target; presetDeferSince = now; } // 目標更新→先送り計測開始
  // 安全窓に入るか、25秒の先送り上限で適用(ボス戦中だけは上限を無視し決着まで待つ)。
  const bossFight = boss.active && !boss.dead;
  if (presetSafeWindow() || (!bossFight && now - presetDeferSince > 25000)) {
    appliedPreset = target;
    pendingPreset = null;
    applyPreset(target);
  }
}

// 診断用: tick の生存(tick++)・到達ステージ・例外を共有して、固まった瞬間の状態を可視化する。
const __dg: any = ((window as any).__diag = (window as any).__diag || { tick: 0, err: 0, lastErr: '', stage: '-' });
function tick(): void {
  // どこかで例外が出ても次フレームを必ず予約する（フレーム内例外でループが死に、
  // 景色=ミラー描画だけ動いて移動・カメラが固まる症状を防ぐ）。
  __dg.tick++; __dg.stage = 'enter';   // tick が呼ばれた事実を無条件に記録（例外有無に関係なく）
  try {
    const now = performance.now();
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    __dg.stage = 'move';
    if (titleMode) {
      // タイトル: メイン島の周りをゆっくり周回するシネマティックカメラ
      titleAngle += dt * 0.045;
      player.pos.x = 500 + Math.cos(titleAngle) * 1500;
      player.pos.z = -500 + Math.sin(titleAngle) * 1500;
      player.pos.y = 130;
      player.lookAt(500, 10, -500);
    }
    if (player.enabled && !player.frozen && stats.alive && !hud.uiOpen()) stepMovement(dt);
    __dg.stage = 'sync';
    syncCamera();   // カメラをプレイヤー姿勢へ固定同期（海面構築がこれを読む）

    const movingHard = !!(player.keys['KeyW'] || player.keys['KeyA'] || player.keys['KeyS'] || player.keys['KeyD']);
    const sprinting = !!(player.keys['ShiftLeft'] || player.keys['ShiftRight']) && canSprint();
    const tod = timeOfDay(), isNight = tod < 0.22 || tod >= 0.80;
    const diving = player.mode === 'dive' && !titleMode && !player.frozen;
    if (!titleMode) tickSurvival(dt, { sprinting, movingHard, drainMul: isNight ? 1.35 : 1, diving, oxygenMul: (inventory['pearl_charm'] || 0) > 0 ? 0.6 : 1 }); // 夜は冷えて消耗が速い/潜水は酸素が減る(真珠の御守で長持ち)。タイトル中は消耗させない
    o2Tick(diving); // 酸素ゲージ(潜水中のみ表示)
    if (stats.alive) tickTime(dt);
    // 日付の告知: 深夜0時の自然繰り上がりが無告知で進行感が薄かった(就寝は専用トーストが別途出る)
    if (!titleMode && stats.alive && clock.day !== announcedDay) {
      if (announcedDay > 0 && clock.day > announcedDay) {
        hud.toast(`📅 Day ${clock.day} — 生き延びて${clock.day}日目`);
        if (clock.day >= 3) award('day3');
        if (clock.day >= 7) award('week_survivor');
        spawnMorningFlotsam(); // 朝の浜に漂着物(毎朝浜を見に行く日課のループ)
      }
      announcedDay = clock.day;
    }
    // 初上陸の発見演出: 新しい島に立った瞬間、島名を映画的に出す(発見のワクワク)
    if (!titleMode && stats.alive && player.mode === 'land') {
      const isl = islandAt(player.pos.x, player.pos.z);
      if (isl && !visitedIslands.includes(isl)) {
        visitedIslands.push(isl); saveProgress();
        sfx('discover');
        hud.showPlaceName(isl, visitedIslands.length === 1 ? '— 漂着した島 —' : '— 新しい島に上陸 —');
        if (visitedIslands.length >= 2) award('new_island');
        if (visitedIslands.length >= 3 + customIslands.length) award('all_islands'); // 全島=クローン3+自前地形
      }
    }
    // カモメ: 昼の陸/水面でときどき遠くに鳴く(世界の気配)。夜・雨・潜水中は鳴かない。
    // 条件外でも次回時刻は進める=夜明け直後にいきなり鳴かない。
    if (!titleMode && stats.alive && now > nextGullAt) {
      nextGullAt = now + 18000 + Math.random() * 27000;
      const gtod = timeOfDay();
      if (gtod >= 0.25 && gtod < 0.75 && !weather.raining && (player.mode === 'land' || player.mode === 'swim')) sfx('gull');
    }
    // いかだの波揺れ(乗っていればカメラも揺れる=stepMovementがy追従)
    // 波高計(拝借したブイ)をいかだの真下へ追従させ、ミラーの浮力が書いた y と傾きをコピー=本物の波。
    {
      const t = now * 0.001;
      for (let i = 0; i < rafts.length; i++) {
        const r = rafts[i];
        const pb = (r as any).__probe;
        if (pb) {
          pb.position.x = r.position.x; pb.position.z = r.position.z; // 次フレームここで波高が書かれる
          r.position.y = pb.position.y + RAFT_FLOAT;                  // 波高(ブイ喫水込み) + いかだ喫水
          const e = (r as any).__pe || ((r as any).__pe = new (r.rotation.constructor as any)());
          e.setFromQuaternion(pb.quaternion, 'YXZ');                  // うねりの傾き(pitch/roll)だけ拝借
          r.rotation.x = e.x; r.rotation.z = e.z;                     // ヨー(rotation.y)は自前の進行方向
        } else { // プローブが無い時のフォールバック(フェイク揺れ)
          r.position.y = RAFT_FLOAT + Math.sin(t * 1.1 + i * 2.1) * 1.6;
          r.rotation.x = Math.sin(t * 0.9 + i) * 0.016;
          r.rotation.z = Math.cos(t * 0.7 + i * 1.3) * 0.02;
        }
        // 帆の風はらみ: 膨らみの脈動+わずかな煽り。嵐は強く速く(生きた布に見せる)
        const sailM = (r as any).__sail || ((r as any).__sail = r.getObjectByName('raft-sail'));
        if (sailM) {
          const gust = weather.raining ? 1.8 : 1;
          sailM.scale.z = 1 + 0.10 * gust * Math.sin(t * 1.7 * gust + i * 2.4) + 0.03 * Math.sin(t * 5.3 + i);
          sailM.rotation.y = 0.05 * gust * Math.sin(t * 0.8 + i * 1.7);
        }
      }
    }
    // 焚き火の炎を揺らめかせる(scale脈動+ゆっくり回転)。雨は火勢が弱まる(熾火は保つ=休憩/調理は不変)
    {
      fireRainMul += ((weather.raining ? 0.6 : 1) - fireRainMul) * Math.min(1, dt * 0.8);
      const emberOp = Math.max(0, (fireRainMul - 0.6) / 0.4); // 雨では火の粉が飛ばない
      const ft = now * 0.001;
      for (let i = 0; i < placed.length; i++) {
        const pl2 = placed[i];
        if (pl2.type !== 'campfire') continue;
        pl2.obj.traverse((o: any) => {
          if (o.name !== 'campfire-flame') return;
          o.scale.y = (0.85 + 0.25 * Math.sin(ft * 9 + i * 2 + o.rotation.y * 3) + 0.08 * Math.sin(ft * 23 + i)) * fireRainMul;
          o.scale.x = 0.9 + 0.15 * Math.sin(ft * 7 + i + o.rotation.y);
          o.rotation.y += dt * 1.3; // 炎だけゆっくり自転(2枚が同速なので交差は保たれる)
        });
        // 火の粉: 各自の周期で炎の上を螺旋上昇し、頂点で下に戻る(ループ)
        pl2.obj.traverse((o: any) => {
          if (o.name !== 'campfire-ember') return;
          const s = o.userData.seed as number;
          const ph = (ft * (0.55 + 0.09 * s) + s * 0.37) % 1;   // 0→1で1周期
          o.position.set(Math.sin(s * 2.4 + ph * 9) * (1.5 + s * 0.2), 6 + ph * 16, Math.cos(s * 1.9 + ph * 8) * (1.5 + s * 0.15));
          o.material.opacity = 0.9 * (1 - ph) * emberOp;         // 上るほど消える
          o.rotation.z = ph * 7 + s;
        });
      }
    }
    const stillRate = WATER_RATE * (weather.raining ? RAIN_STILL_MUL : 1); // 雨は給水チャンス
    for (const p of placed) if (p.type === 'water_still') {
      p.water = Math.min(WATER_MAX, p.water + stillRate * dt);
      // 貯まると水面が見え(見える=1杯以上=飲める)、水位も貯量で上下(1杯=沈み気味〜3杯=縁いっぱい)
      p.obj.traverse((o: any) => { if (o.name === 'still-water') { o.visible = p.water >= 1; o.position.y = -(1 - Math.min(1, p.water / WATER_MAX)) * 1.2; } });
    }
    tickWeather(dt);
    tickShark(dt);
    tickBoss(dt);
    bossBarTick(); // 主の体力バー(戦闘中のみ)
    if (attackCooldown > 0) attackCooldown = Math.max(0, attackCooldown - dt);
    heldTick(now, dt); // 一人称の手持ちアイテム(銛/斧)を目線に追従+振りアニメ
    stepSfxTick(sprinting); // 足音(陸)/水かき(遊泳)
    tickFishing(dt);
    fishFloatTick(now); // 釣りウキ(拝借ブイの貸借含む)
    bubbleTick(dt);     // 開封の泡バースト
    // 環境音: ヌシ戦=嵐(緊迫) > 潜水=水中 > 雨=嵐 > 通常=穏やかな波
    setAmbience(boss.active && !boss.dead ? 'storm' : player.mode === 'dive' ? 'underwater' : weather.raining ? 'storm' : 'calm');

    __dg.stage = 'hud';
    if (escaped && escapedShip) { escapedShip.position.x -= dt * 26; escapedShip.position.z -= dt * 10; } // 出航(去っていく)
    // (旧: 水中の浮遊粒子motes=上向き三角形は「リアルじゃない」ため削除。ユーザー指摘2026-07-07)
    tickDayNightPreset(now);                                  // 夜=moonlitプリセット(本物の星空)
    if (now > nextFloorEnforce) { nextFloorEnforce = now + 1500; enforceOceanFloorPlane(); enforceMobileShadowMapSize(); } // 再構築で平面/シャドウが戻るのを補正し続ける
    // 夜/嵐の自動露出補正(暗順応)。焚き火の近くではさらに明るい=火明かりの体験
    {
      let expo = presetState === 'night' ? 1.55 : presetState === 'storm' ? 1.3 : 1.0;
      if (presetState !== 'day') {
        for (const pl2 of placed) {
          if (pl2.type !== 'campfire') continue;
          const d = Math.hypot(pl2.x - player.pos.x, pl2.z - player.pos.z);
          if (d < 160) { expo += 0.3 * (1 - d / 160); break; } // 焚き火の火明かり(8m以内)
        }
      }
      // 水中/松明の視界確保: レンダラのトーンマッピング露出を増感する(CSS brightnessは暗部の階調が出ない)。
      // dive中はdepth(world, 水面下でプラス)を、浮上時は-1を渡して既定露出へ復元する。松明は手持ち中のみ照らす。
      applyExposureBoost(player.mode === 'dive' ? Math.max(0, -player.pos.y) : -1, torchHeld && presetState !== 'day');
      const _dbgExp = (window as any).__dbgExposure;
      hud.setExposure(_dbgExp != null ? _dbgExp : Math.min(expo, 2.0)); // デバッグ: 明るさ手動固定
      hud.setRain(weather.raining && player.mode !== 'dive'); // 水中では雨線を消す(海中に雨は降らない)
    }
    hud.setDayTint(presetState === 'night' ? 0.5 : tod);      // 本物の夜空がある間はDOMの色調は透明(0.5=昼)
    hud.setBarWarnings(stats.hunger < 22, stats.thirst < 22, isExhausted()); // 飢餓/渇き/スタミナ枯渇の警告点滅
    // 危機前の実践ヒント(点滅[22]より早い35で対処の時間を残す): 手段を持っていない時だけ、何をすべきかを1回示す
    if (!hintedFood && stats.alive && !titleMode && stats.hunger < 35) {
      hintedFood = true;
      if (!Object.keys(ITEMS).some((k) => ITEMS[k].food && (inventory[k] || 0) > 0)) {
        hud.toast((inventory['fishing_rod'] || 0) > 0
          ? (IS_TOUCH ? '🎣 空腹だ — 竿を装備して水辺で「使う」、釣りで食料を' : '🎣 空腹だ — 水辺で Q、釣りで食料を確保しよう')
          : '🍖 空腹だ — 椰子の実を採るか、釣り竿をクラフトしよう');
      }
    }
    if (!hintedWater && stats.alive && !titleMode && stats.thirst < 35) {
      hintedWater = true;
      if (!placed.some((p) => p.type === 'water_still') && (inventory['coconut'] || 0) === 0) {
        hud.toast('💧 喉が渇く — 給水器をクラフトして設置しよう（雨は好機）');
      }
    }
    if (stats.health < prevHealth - 0.5) { hud.flashDamage(); if (prevHealth - stats.health > 3) sfx('hurt'); } // 被ダメ演出
    // 低体力(25未満)の心音: 2.2秒周期。体力が減るほど本能的に危機を知らせる
    if (stats.alive && stats.health < 25 && now - lastHeartbeat > 2200) { lastHeartbeat = now; sfx('heartbeat'); }
    hud.setDanger(stats.alive && !titleMode && stats.health < 25); // 心音と同周期の赤ビネット
    prevHealth = stats.health;
    hud.updateStats(stats);
    hud.updateClock(`Day ${clock.day}  ${clockLabel()}  ${phaseLabel()}`);
    // 潜水中は深度計(m換算)を表示: 深海クレートや海溝の主へ潜る際の目安
    const depthTag = player.mode === 'dive' && player.pos.y < 0 ? ` <b style="color:#8fd0ff">-${Math.round(-player.pos.y / WORLD_PER_M)}m</b>` : '';
    hud.setMode(`${modeLabel(player.mode)}${depthTag}<span class="co">  x${toM(player.pos.x)} y${toM(player.pos.y)} z${toM(player.pos.z)}m</span>`);
    const tgt = targetNode();
    const np = nearestPlaced();
    const nc = nearestCrate();
    hud.setPrompt(
      hud.uiOpen() ? '' // パネル(クラフト/実績)中はインタラクト不可のため重ね表示しない
        : fishBite > 0 ? '❗<kbd>E</kbd>合わせろ！'
        : fishing > 0 ? '🎣 アタリを待っている…'
        : riding ? '<kbd>E</kbd>降りる'
        : nearestRaft() ? '<kbd>E</kbd>いかだに乗る'
          : (nearShip() && repairRemaining() > 0) ? `<kbd>E</kbd>船を修理 ${repairLabel()}`
            : np ? (np.type === 'campfire' ? '<kbd>E</kbd>朝まで休む' : np.type === 'shelter' ? '<kbd>E</kbd>ぐっすり休む(全回復)' : `<kbd>E</kbd>水を飲む (${Math.floor(np.water)}杯${weather.raining ? '・☔速く貯まる' : ''})`)
              : nc ? (nc.tier === 'flotsam' ? '<kbd>E</kbd>🌊 漂着物を調べる' : nc.tier === 'relic' ? '<kbd>E</kbd>🗺 船乗りの遺品を開ける' : '<kbd>E</kbd>📦 沈没物資を開ける')
                : tgt ? `<kbd>E</kbd>採取 ${tgt.def.label}`
                  : (inventory['raft_floor'] || 0) > 0 ? '<kbd>R</kbd>いかだを置く'
                    : '',
    );
    tickGuide();
    hud.setGoal(escaped ? '' : guideText());
    // コンパス: 船と各島の相対方位（正面=中央）。近すぎる目標は非表示。
    {
      const relTo = (tx: number, tz: number) => {
        // 正=右手の目標(帯の右に描く)。yaw−targetYawの順(右旋回=yaw減少でrelが0へ寄る)。
        // 旧: targetYaw−yaw は正=反時計回り=左手で、帯が左右鏡像だった(「右に見えるのに右旋回で遠のく」実バグ)
        let r = player.yaw - Math.atan2(-(tx - player.pos.x), -(tz - player.pos.z));
        while (r > Math.PI) r -= 2 * Math.PI; while (r < -Math.PI) r += 2 * Math.PI;
        return r;
      };
      const marks: { key: string; icon: string; label: string; rel: number; dist?: number }[] = [];
      if (!escaped) marks.push({ key: 'ship', icon: '⛵', label: '難破船', rel: relTo(SHIP.x, SHIP.z), dist: Math.round(Math.hypot(SHIP.x - player.pos.x, SHIP.z - player.pos.z) / WORLD_PER_M) });
      for (const o of ISLAND_OFFSETS) {
        const cx = 500 + o.dx, cz = -500 + o.dz;
        const d = Math.hypot(cx - player.pos.x, cz - player.pos.z);
        if (d > 900) marks.push({ key: 'i' + o.name, icon: '🏝', label: o.name, rel: relTo(cx, cz), dist: Math.round(d / WORLD_PER_M) }); // 島の上/至近では出さない
      }
      for (const ci of customIslands) {
        const d = Math.hypot(ci.cx - player.pos.x, ci.cz - player.pos.z);
        if (d > 900) marks.push({ key: 'i' + ci.name, icon: '⛰', label: ci.name, rel: relTo(ci.cx, ci.cz), dist: Math.round(d / WORLD_PER_M) });
      }
      // 山頂の見晴らし: 高所(山岳島の上部)に立つと、60秒間すべての沈没物資がコンパスに現れる(登山の報酬)
      if (player.mode === 'land' && player.pos.y > 240 && performance.now() > vistaUntil) {
        vistaUntil = performance.now() + 60000;
        sfx('win');
        hud.toast('🔭 見晴らしがいい！ 島々の沈没物資の在り処が見えた（60秒）');
        award('peak_view');
      }
      if (player.mode === 'land' && player.pos.y > 1200) award('summit'); // 天峰島(峰1300)の頂=世界の最高点
      const vista = performance.now() < vistaUntil;
      // 発見の誘導: 通常クレートは範囲内の最寄り1つ(📦)、財宝は距離無制限のビーコン(💎)を全表示
      {
        let best: Crate | null = null, bd = 1500 * 1500;
        for (const c of crates) {
          if (c.opened) continue;
          if (c.tier === 'treasure') { // 財宝=常時ビーコン(遠くても目標として見える)。主が守る財宝は👑
            const d = Math.hypot(c.x - player.pos.x, c.z - player.pos.z);
            const isBoss = c.id === boss.crateId && !boss.dead;
            if (d > 260) marks.push({ key: 'tr' + c.id, icon: isBoss ? '👑' : '💎', label: isBoss ? '主の財宝' : '深海の財宝', rel: relTo(c.x, c.z), dist: Math.round(d / WORLD_PER_M) });
            continue;
          }
          if (c.tier === 'relic') { // 手紙の海域=常時❓ビーコン(手がかりを追う冒険の目標)
            const d = Math.hypot(c.x - player.pos.x, c.z - player.pos.z);
            if (d > 220) marks.push({ key: 'rl' + c.id, icon: '❓', label: '手がかりの海域', rel: relTo(c.x, c.z), dist: Math.round(d / WORLD_PER_M) });
            continue;
          }
          if (c.tier === 'flotsam') { // 漂着物=浜の近くでだけ🎁(遠くから引っ張る目標ではない)
            const d = Math.hypot(c.x - player.pos.x, c.z - player.pos.z);
            if (d < 800 && d > 60) marks.push({ key: 'fl' + c.id, icon: '🎁', label: '漂着物', rel: relTo(c.x, c.z), dist: Math.round(d / WORLD_PER_M) });
            continue;
          }
          if (vista) { marks.push({ key: 'cr' + c.id, icon: '📦', label: '沈没物資', rel: relTo(c.x, c.z) }); continue; } // 見晴らし中は全表示
          const dx = c.x - player.pos.x, dz = c.z - player.pos.z, d2 = dx * dx + dz * dz;
          if (d2 < bd) { bd = d2; best = c; }
        }
        if (best) {
          marks.push({ key: 'crate', icon: '📦', label: '沈没物資', rel: relTo(best.x, best.z), dist: Math.round(Math.sqrt(bd) / WORLD_PER_M) });
          if (!salvageHintShown && bd < 520 * 520) { // 接近したら一度だけ潜水を促す
            salvageHintShown = true;
            hud.toast(IS_TOUCH ? '📦 海底に物資が沈んでいる。「潜る」で潜って「調べる」で回収しよう' : '📦 海底に物資が沈んでいる。潜って（Ctrl）E で回収しよう');
          }
        }
      }
      hud.updateCompass(marks);
    }
    __dg.stage = 'markers';
    if (game.camera) hud.updateMarkers(game.camera, window.innerWidth, window.innerHeight, player, tgt);

    // オートセーブ（15秒毎）。潜水中は保存しない: 深海・低O2の状態が保存されると
    // 「続きから」で溺れながら目覚める罠になる(浮上すれば次周期で保存される)
    if (stats.alive && !titleMode && player.mode !== 'dive' && now - lastSave > 15000) { lastSave = now; if (saveState(player, nodes, repair, placedData())) { saveProgress(); hud.toast('💾 セーブ'); } }

    // 死亡処理（1度だけ）: セーブを消して新規開始できるように
    if (!stats.alive && !deadHandled) {
      deadHandled = true; player.mode = 'dead';
      sfx('death');
      hud.setDanger(false); // 低HPビネットを消す(死亡後は低HPセクションが走らない)
      clearSave();
      // 死因別に「次はこう凌ぐ」を一行添える(死をチュートリアルにする)。主張は全てコード検証済み
      const nowMs = performance.now();
      const kind = (nowMs - lastBiteAt < 4000) ? (lastBiteBoss ? 'boss' : 'shark')
        : (nowMs - lastFallAt < 4000) ? 'fall'
        : (stats.oxygen <= 0) ? 'drown'
        : (stats.thirst <= 0) ? 'thirst'
        : (stats.hunger <= 0) ? 'hunger' : 'other';
      const cause = { boss: '👑 海溝の主に食われた', shark: '🦈 サメに襲われた', fall: '🦵 高所から落ちた', drown: '🫧 溺れてしまった', thirst: '💧 渇きに倒れた', hunger: '🍖 飢えに倒れた', other: '力尽きた' }[kind];
      const tip = {
        boss: '主は海溝の最深部にいる。銛を持ち、体力万全で挑もう',
        shark: 'サメは浅瀬や陸までは追ってこない。銛があれば反撃できる',
        fall: '高い場所からは水面へ飛び込めば無傷で降りられる',
        drown: 'O2が切れる前に浮上を。水面に出れば素早く回復する',
        thirst: '給水器をクラフトして設置すれば真水が溜まる。雨も潤いになる',
        hunger: '釣りや焚き火の調理で食料を確保。椰子の実でもしのげる',
        other: '満腹と水分を40より上に保てば体力は自然に回復する',
      }[kind];
      hud.showDeath(true, clock.day, `${cause}<br><small style="opacity:.78">💡 ${tip}</small><br>${statsText()}`);
      if (document.exitPointerLock) document.exitPointerLock();
    }
    __dg.stage = 'end';
  } catch (err) {
    __dg.err++; __dg.lastErr = String((err && (err as any).message) || err);
    console.error('[game] tick error (loop kept alive):', err);
  } finally {
    requestAnimationFrame(tick);
  }
}

function stepMovement(dt: number): void {
  const k = player.keys;

  // ---- いかだ搭乗中: WASDでいかだごと水平移動。プレイヤーは甲板中央に立つ ----
  if (riding) {
    const size = riding.scale?.x || 1;
    const sinY = Math.sin(player.yaw), cosY = Math.cos(player.yaw);
    const fx = -sinY, fz = -cosY, rx = cosY, rz = -sinY;
    let fwd = (k['KeyW'] ? 1 : 0) - (k['KeyS'] ? 1 : 0) - player.tmove.y;
    let strafe = (k['KeyD'] ? 1 : 0) - (k['KeyA'] ? 1 : 0) + player.tmove.x;
    fwd = Math.max(-1, Math.min(1, fwd)); strafe = Math.max(-1, Math.min(1, strafe));
    const ix = fx * fwd + rx * strafe, iz = fz * fwd + rz * strafe;
    const inLen = Math.hypot(ix, iz);
    const sprint = (k['ShiftLeft'] || k['ShiftRight']) && canSprint();
    const sp = player.eyeHeight * 12 * (sprint ? RUN_MUL * 0.7 : 1); // 遊泳より少し遅い
    if (inLen > 1e-4) {
      const mx = ix / inLen, mz = iz / inLen;
      const nx = riding.position.x + mx * sp * dt, nz = riding.position.z + mz * sp * dt;
      // 座礁防止: 移動先の地面が海面より上(=浜・陸)なら進まない。海面下(浅瀬含む)は航行可。
      // 斜めに海岸へ触れた時は空いている軸成分だけ滑らせる(グレージングのたびにデッドストップしない)
      if (groundAt(nx, nz) < SEA_LEVEL - 1) {
        riding.position.x = nx;
        riding.position.z = nz;
        gameStats.sailed += sp * dt;
      } else if (Math.abs(mx) > 1e-4 && groundAt(nx, riding.position.z) < SEA_LEVEL - 1) {
        riding.position.x = nx;
        gameStats.sailed += Math.abs(mx) * sp * dt;
      } else if (Math.abs(mz) > 1e-4 && groundAt(riding.position.x, nz) < SEA_LEVEL - 1) {
        riding.position.z = nz;
        gameStats.sailed += Math.abs(mz) * sp * dt;
      }
      // 船首を進行方向へゆっくり旋回（帆・マストが常に背後に来る）
      const targetYaw = Math.atan2(-mx, -mz);
      let dy = targetYaw - riding.rotation.y;
      while (dy > Math.PI) dy -= 2 * Math.PI; while (dy < -Math.PI) dy += 2 * Math.PI;
      riding.rotation.y += dy * Math.min(1, dt * 2.5);
    }
    // 船首寄り(帆・マストより前)に立つ = 前方視界を帆で塞がない
    const BOW = 16 * size;
    player.pos.x = riding.position.x - Math.sin(riding.rotation.y) * BOW;
    player.pos.z = riding.position.z - Math.cos(riding.rotation.y) * BOW;
    player.pos.y = riding.position.y + RAFT_DECK * size; // 甲板の上に立つ
    player.vy = 0; player.grounded = true; player.mode = 'raft'; player._bob = 0;
    player._shake = Math.max(0, player._shake - dt * 0.15); // 搭乗中も揺れを減衰(ここでreturnするため末尾の減衰に届かない)
    return;
  }

  // 速度は身長(eyeHeight)に比例
  const E = player.eyeHeight;
  const WALK = E * WALK_K, SWIM = E * SWIM_K, SWIM_VERT = E * SWIM_K, GRAV = E * GRAV_K, JUMP = E * JUMP_K;
  const sinY = Math.sin(player.yaw), cosY = Math.cos(player.yaw);
  const fx = -sinY, fz = -cosY;     // 前方(水平)
  const rx = cosY, rz = -sinY;      // 右
  // 入力: キー(WASD) + タッチジョイスティック(tmove)。tmove.y上=前進
  let fwd = (k['KeyW'] ? 1 : 0) - (k['KeyS'] ? 1 : 0) - player.tmove.y;
  let strafe = (k['KeyD'] ? 1 : 0) - (k['KeyA'] ? 1 : 0) + player.tmove.x;
  fwd = Math.max(-1, Math.min(1, fwd));
  strafe = Math.max(-1, Math.min(1, strafe));
  const ix = fx * fwd + rx * strafe;
  const iz = fz * fwd + rz * strafe;
  const inLen = Math.hypot(ix, iz);
  const sprint = (k['ShiftLeft'] || k['ShiftRight']) && canSprint();

  const gh = groundAt(player.pos.x, player.pos.z);
  const hasLand = gh > SEA_LEVEL + 0.15;

  if (hasLand) {
    // ---- 陸（島）----
    const sp = WALK * (sprint ? RUN_MUL : 1);
    if (inLen > 1e-4) {
      const nx = player.pos.x + (ix / inLen) * sp * dt;
      const nz = player.pos.z + (iz / inLen) * sp * dt;
      const ghNew = groundAt(nx, nz);
      const cur = Math.max(gh, player.pos.y);
      // 段差が大きすぎる(椰子の木・崖)は登れない＝横移動をブロック。海(-Infinity)へは出られる
      if (ghNew === -Infinity || ghNew - cur <= player.eyeHeight * 0.5) { player.pos.x = nx; player.pos.z = nz; }
    }
    const groundNow = groundAt(player.pos.x, player.pos.z);
    const target = groundNow > SEA_LEVEL ? groundNow : SEA_LEVEL;
    // 泳ぎの上昇(vy=SWIM_VERT)を陸に持ち越すと打ち上がるため、上陸時に上向きvyは捨てる
    if (prevMode === 'swim' || prevMode === 'dive') player.vy = Math.min(player.vy, 0);
    // vy>0(ジャンプ直後)は接地スナップしない。旧: 無条件スナップでvyが毎tick消され、ジャンプが一切飛べなかった
    if (player.pos.y <= target + 0.6 && player.vy <= 0) {
      player.pos.y = target; player.vy = 0; player.grounded = true;
      if (k['Space']) { player.vy = JUMP; player.grounded = false; }
      player.mode = 'land';
    } else {
      player.vy -= GRAV * dt; player.pos.y += player.vy * dt; player.grounded = false;
      if (player.vy < fallImpactVy) fallImpactVy = player.vy; // 着地スナップでvyが消える前に最大下降速度を捕捉
      if (player.pos.y < target) { player.pos.y = target; player.vy = 0; }
      player.mode = 'fall';
    }
  } else {
    // ---- 水中/水面（泳ぎ・潜水）----
    const submerged = (player.pos.y + EYE) < SEA_LEVEL - 0.1; // 頭(目線)が水面下か
    const sp = SWIM * (sprint ? RUN_MUL * 0.9 : 1);
    const pitchUp = submerged ? Math.sin(player.pitch) : 0;     // 潜水時は視線方向に上下
    const horiz = submerged ? Math.cos(player.pitch) : 1;
    if (inLen > 1e-4) {
      const nx = player.pos.x + (ix / inLen) * sp * horiz * dt;
      const nz = player.pos.z + (iz / inLen) * sp * horiz * dt;
      // 水中の壁: 移動先の地形が体より十分上に突き出ているなら塞ぐ(緩斜面は床の押し上げで登る)。
      // これがないと深所から崖xzへ入った瞬間、床クランプで崖の上へワープしてしまう。
      const wall = groundAt(nx, nz);
      if (wall === -Infinity || wall <= player.pos.y + E * 0.5) { player.pos.x = nx; player.pos.z = nz; }
      player.pos.y += fwd * pitchUp * sp * dt;
    }
    if (k['ControlLeft'] || k['ControlRight']) { player.vy = -SWIM_VERT; player.mode = 'dive'; }
    else if (k['Space']) { player.vy = SWIM_VERT; player.mode = 'swim'; }
    else {
      const target = SEA_LEVEL - swimDepth(); // 頭が水面に出る位置
      if (player.pos.y < target - E * 1.5) {
        player.vy = E * 1.05; // 深場: ゆっくり自然浮上(ボタンを離しても急浮上しない)
      } else {
        player.vy = (target - player.pos.y) * 4; // 水面近く: 顔を出す位置へスナップ
      }
      player.mode = submerged ? 'dive' : 'swim';
    }
    player.pos.y += player.vy * dt;
    // 海底との衝突: 裾野つき床(floorAt=棚の縁から-240へ連続)を貫通しないよう底で止める。
    // max(bed, SEABED_MIN)で-Infinity(外洋)も吸収し、どの床でもSEABED_MIN未満へは行けない
    // =見える砂平面(SEABED_MIN-5)より必ず上に留まる(貫通が構造的に不可能)。
    const floorY = Math.max(floorAt(player.pos.x, player.pos.z), SEABED_MIN);
    if (player.pos.y < floorY) { player.pos.y = floorY; player.vy = 0; }
    if (player.pos.y > SEA_LEVEL + 1.5) { player.pos.y = SEA_LEVEL + 1.5; player.vy = 0; }
    resolveShipCollision(); // 難破船の船体は素通りできない(真下からの浮上・横からの侵入)
  }

  if (prevMode === 'dive' && player.mode !== 'dive') hud.splashWet(); // 浮上した瞬間、顔から水が滴る
  // 水際の音: 入水スプラッシュ(陸/落下→水)と高所からの着地ドスン。微小バウンド(fall<380ms)は鳴らさない。
  if (!titleMode && stats.alive) {
    const nowMs = performance.now();
    if ((prevMode === 'land' || prevMode === 'fall') && (player.mode === 'swim' || player.mode === 'dive') && nowMs > splashMuteUntil) {
      splashMuteUntil = nowMs + 900; // 波打ち際のモード往復で連発しない
      sfx('splash');
    }
    if (prevMode !== 'fall' && player.mode === 'fall') fallSince = nowMs;
    if (prevMode === 'fall' && player.mode === 'land') {
      if (nowMs - fallSince > 380) sfx('thud');
      // 高所落下ダメージ: 衝突速度ベース。-140(≈6.4m自由落下)まで無傷=通常ジャンプ(-36.8)は安全。
      // 水面への着水はfall→swim遷移なのでここを通らず常に無傷(水がクッション)。
      if (fallImpactVy < -140) {
        const dmg = Math.min(45, Math.round((-fallImpactVy - 140) * 0.25));
        if (dmg > 0) {
          damage(dmg); lastFallAt = nowMs;
          hud.updateStats(stats);
          hud.toast('🦵 高所から落ちた！');
        }
      }
    }
    if (player.mode !== 'fall') fallImpactVy = 0; // 次の落下に備えてリセット
  }
  prevMode = player.mode;
  player._bob = (player.mode === 'swim') ? Math.sin(performance.now() * 0.0016) * 0.18 : 0;
  player._shake = Math.max(0, player._shake - dt * 0.15); // 採取ヒットの揺れを減衰
}

function modeLabel(m: string): string {
  return ({ swim: '🏊 遊泳', dive: '🤿 潜水', land: '🏝 上陸', fall: '↓ 落下', dead: '💀', raft: '🛶 いかだ' } as Record<string, string>)[m] || m;
}

// --- カメラ乗っ取り（ロック方式・海面整合）----------------------------------
// 海面(クリップマップ)はミラーのtick内で _camera.position(グリッド中心)と
// getWorldDirection()(near水面クリップ法線)を読む。描画直前の上書きでは間に合わず
// 楔状に欠ける。よってカメラの position/quaternion をプレイヤー姿勢に「固定」し、
// ミラーのカメラ制御(step2/4)が動かせないようにする → tickの海面構築もプレイヤー基準で整合。
const _pose = { px: 0, py: 0, pz: 0, qx: 0, qy: 0, qz: 0, qw: 1 };

function computePoseFromPlayer(): void {
  _pose.px = player.pos.x;
  _pose.py = player.pos.y + player.eyeHeight + player._bob;
  // 潜水中は目線を海面下に保つ。浅い海底(水深<目線)に着底しても頭が海面を突き抜けて
  // 「海底を貫通して海面が見える」状態になるのを防ぐ(目線EYEが浅瀬の水深より大きいため)。
  // ただし床が-2より浅い超浅瀬では、このクランプがカメラを砂の中へ押し込む(=貫通して
  // 地面の裏が見える)ため、床のわずか上を下限にする。
  if (player.mode === 'dive') {
    _pose.py = Math.min(_pose.py, SEA_LEVEL - 2);
    const bed = Math.max(floorAt(player.pos.x, player.pos.z), SEABED_MIN);
    _pose.py = Math.max(_pose.py, bed + 0.9);
  }
  _pose.pz = player.pos.z;
  // quaternion = Euler(pitch+shake, yaw, 0, 'YXZ')（roll=0）
  const pitch2 = player.pitch + player._shake * Math.sin(performance.now() * 0.09);
  const c1 = Math.cos(pitch2 * 0.5), s1 = Math.sin(pitch2 * 0.5);
  const c2 = Math.cos(player.yaw * 0.5), s2 = Math.sin(player.yaw * 0.5);
  _pose.qx = s1 * c2; _pose.qy = c1 * s2; _pose.qz = -s1 * s2; _pose.qw = c1 * c2;
}

// ロックを回避してフィールド直書きで姿勢を適用
function applyPoseToCamera(cam: any): void {
  cam.position.x = _pose.px; cam.position.y = _pose.py; cam.position.z = _pose.pz;
  cam.quaternion.x = _pose.qx; cam.quaternion.y = _pose.qy; cam.quaternion.z = _pose.qz; cam.quaternion.w = _pose.qw;
}

function lockCamera(cam: any): void {
  if (!cam || cam.__gameLocked) return;
  const noop = function (this: any) { return this; };
  const lock = (o: any, names: string[]) => { for (const n of names) if (typeof o[n] === 'function') o[n] = noop; };
  // ミラーのCameraRig/controlsがこのカメラを動かすのを遮断（メソッド経由の書き込みを無効化）
  lock(cam.position, ['copy', 'set', 'setX', 'setY', 'setZ', 'add', 'addScaledVector', 'addVectors', 'sub', 'subVectors', 'lerp', 'lerpVectors', 'applyMatrix4', 'applyQuaternion', 'setFromSpherical', 'setFromSphericalCoords', 'setFromMatrixPosition', 'setFromMatrixColumn', 'fromArray', 'multiplyScalar', 'multiply']);
  lock(cam.quaternion, ['copy', 'set', 'setFromEuler', 'setFromAxisAngle', 'setFromRotationMatrix', 'setFromUnitVectors', 'multiply', 'premultiply', 'multiplyQuaternions', 'slerp', 'slerpQuaternions', 'rotateTowards', 'fromArray', 'normalize', 'invert', 'conjugate']);
  // matrixWorld 更新時に必ずプレイヤー姿勢を書き込む（getWorldDirectionはmatrixWorldから導出）
  const origUMW = cam.updateMatrixWorld.bind(cam);
  cam.updateMatrixWorld = function (force?: boolean) {
    if (player.enabled) applyPoseToCamera(cam);
    return origUMW(force);
  };
  cam.matrixAutoUpdate = true;
  cam.__gameLocked = true;
}

function engageCamera(): void {
  game.onFrame = null;          // 旧・描画直前フックは使わない（補助パスのカメラ巻き込みを避ける）
  computePoseFromPlayer();
  lockCamera(game.camera);
}

// 毎フレーム(tick)でカメラをプレイヤー姿勢へ同期。カメラ差し替え時は再ロック。
function syncCamera(): void {
  const cam = game.camera;
  if (!cam) return;
  if (!cam.__gameLocked) lockCamera(cam);
  if (!player.enabled) return;
  computePoseFromPlayer();
  applyPoseToCamera(cam);
  cam.updateMatrixWorld(true);
}

// 見通しの良い開けた浜スポット（低い陸・海が近い・周囲に高い岩壁が無い）を heightfield から探す。
type BeachSpot = { x: number; y: number; z: number; sx: number; sz: number };
let spawnBeach: BeachSpot | null = null;
function findOpenBeach(): BeachSpot | null {
  if (!hf) return null;
  const N = hf.N, H = hf.H, R = 5;
  let best: BeachSpot | null = null, bestScore = -Infinity;
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const h = H[j * N + i];
      if (!(h > SEA_LEVEL + 0.5)) continue;                    // 陸のみ
      let maxNear = h, water = 0, wsx = 0, wsz = 0;
      for (let dj = -R; dj <= R; dj++) for (let di = -R; di <= R; di++) {
        const ii = i + di, jj = j + dj;
        // グリッド外(=島bboxの外=外洋) と 内部の-Infinity(水) をどちらも「水(海方向)」として集計
        if (ii < 0 || jj < 0 || ii >= N || jj >= N) { water++; wsx += di; wsz += dj; continue; }
        const hh = H[jj * N + ii];
        if (hh <= SEA_LEVEL) { water++; wsx += di; wsz += dj; } // 海面下(=海/水。-Infinity含む)も水として扱う
        else if (hh > maxNear) maxNear = hh;
      }
      if (water < 3) continue;                                 // 海が近い岸だけ
      // 見通し = 周囲の起伏(maxNear-h)が小さいほど良い。低い陸・海が近いほど加点。
      const score = -(maxNear - h) * 1.5 - h * 0.4 + water * 0.5;
      if (score > bestScore) {
        bestScore = score;
        const wl = Math.hypot(wsx, wsz) || 1;
        best = { x: hf.minX + (i + 0.5) * hf.cellX, y: h, z: hf.minZ + (j + 0.5) * hf.cellZ, sx: wsx / wl, sz: wsz / wl };
      }
    }
  }
  return best;
}

// 開けた浜に立たせ、海の方を向く（見通しの良い陸を優先。無ければ最も低い資源ノードへフォールバック）
function spawnOnBeach(): void {
  const s = spawnBeach || (spawnBeach = findOpenBeach());
  if (s) {
    player.teleport(s.x, s.y + 0.6, s.z);
    player.lookAt(s.x + s.sx * 120, s.y - 10, s.z + s.sz * 120); // 海の方向
    return;
  }
  let b: ResourceNode | null = null;
  for (const n of nodes) if (!b || n.y < b.y) b = n;
  if (b) { player.teleport(b.x, b.y + 0.6, b.z); const ox = b.x - 500, oz = b.z + 500; player.lookAt(b.x + ox * 3, b.y - 6, b.z + oz * 3); }
  else { player.teleport(200, 8, -300); player.lookAt(0, -4, 0); }
}

// --- 起動 -------------------------------------------------------------------
function start(): void {
  prewarmPreset();                                     // 最初に時刻相当プリセットを発火。ミラーの再生成(約0.5秒)を島生成と並行させ、ローディング中に隠す
  hf = buildIslandHeightfield(game.scene);             // 島の地形高さ場
  initShipCollision();                                 // 難破船hullのOBB衝突
  enforceOceanFloorPlane();                            // 見える砂平面を潜水限界の下へ(貫通の構造的根絶)
  enforceMobileShadowMapSize();                        // タッチ端末はシャドウ4096→2048(発熱対策)
  installAuxRTDownscaler();                            // 反射/屈折RTをcanvas実ピクセルへ(発熱対策)
  hfFloor = hf ? buildFloorField(hf, SEABED_MIN) : null; // 潜水床(裾野つき)。配置はhf/groundAtのまま
  // 実アセット採取: 椰子→木/草→繊維/岩→石。失敗時は従来の手続き散布にフォールバック。
  islandRes = buildIslandResources(game.scene, hf);
  if (islandRes && islandRes.nodes.length >= 4) {
    nodes = islandRes.nodes;
  } else {
    islandRes = null;
    nodes = placeResourceNodes(hf, { count: 40, seed: 20240629 });
    ensureTypesNearSpawn(nodes);
  }

  // 複数の島: メイン島Groupをcloneして沖へ平行移動配置し、資源ノードも登録(いかだで渡れる)
  if (islandRes) {
    const island = findIsland(game.scene);
    if (island) {
      for (const o of ISLAND_OFFSETS) {
        if (!o.dx && !o.dz) continue; // メイン島はそのまま
        const c = island.clone(true);
        c.position.x += o.dx; c.position.y += o.dy; c.position.z += o.dz; // 平行移動のみ(回転/スケールは地形が壊れる)
        c.name = 'game-island-clone';
        game.scene.add(c);
        // ノード座標の変換。沈めた島(dy<0)で水没した資源は登録しない。
        const mapPoint = (x: number, y: number, z: number): [number, number, number] | null => {
          const ny = y + o.dy;
          if (ny < SEA_LEVEL + 1) return null; // 水没ノードは採取対象外
          return [x + o.dx, ny, z + o.dz];
        };
        const added = islandRes.registerCloneNodes(c, mapPoint);
        console.log('[game] island clone "%s": +%d nodes', o.name, added);
      }
    }
  }

  // 自前地形の山岳島(北の沖)。地形メッシュ+専用hfを生成し、資源(岩多め+椰子/草)を決定論配置。
  if (islandRes) {
    const mt = buildMountainIsland(game.scene, MT.cx, MT.cz, { R: 760, peak: 340, seed: 7 });
    if (mt) {
      customIslands.push({ hf: mt.hf, floorHf: buildFloorField(mt.hf, SEABED_MIN), cx: MT.cx, cz: MT.cz, name: MT.name });
      // 資源: 山肌に岩10 / 南のなだらかな浜〜中腹に椰子4・草6 (座標は決定論LCG)
      let s = 20260702; const rnd = () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
      let rocks = 0, palms = 0, grass = 0;
      for (let i = 0; i < 700 && (rocks < 18 || palms < 4 || grass < 6); i++) {
        const a = rnd() * Math.PI * 2, rr = (0.15 + 0.78 * rnd()) * 760;
        const x = MT.cx + Math.cos(a) * rr, z = MT.cz + Math.sin(a) * rr;
        const h = groundHeight(mt.hf, x, z);
        if (!(h > SEA_LEVEL + 4)) continue;
        if (h <= 50 && palms < 4) { if (islandRes.addPalm(x, z, 'wood', h)) palms++; }          // 椰子=麓(優先)
        else if (h > 55 && rocks < 18) { if (islandRes.addRock(x, z, h, 3 + rnd() * 6)) rocks++; } // 岩=山肌(ボルダー級の大小)
        else if (h <= 55 && grass < 6) { if (islandRes.addPalm(x, z, 'fiber', h)) grass++; }    // 草=中腹
      }
      console.log('[game] mountain island: rocks=%d palms=%d grass=%d', rocks, palms, grass);
    }
  }

  // spawn地点(見通しの良い開けた浜)を確定
  spawnBeach = findOpenBeach();

  // 初動の素材保証: スポーン浜の280以内に椰子/草/岩を揃える。最寄りがコンパスの
  // マーカー圏(320)外だと、最初の数分で素材が見つからず詰み感が出る(実測: 草が413だった)。
  if (islandRes && spawnBeach) {
    const sb = spawnBeach;
    const has = (t: string) => nodes.some((n) => n.type === t && n.amount > 0 && Math.hypot(n.x - sb.x, n.z - sb.z) < 280);
    const put = (kind: 'wood' | 'fiber' | 'rock') => {
      for (const d of [60, 90, 130, 170, 220]) { // 内陸方向へ接地できるまで下がる
        const x = sb.x - sb.sx * d, z = sb.z - sb.sz * d;
        if (kind === 'rock' ? islandRes!.addRock(x, z) : islandRes!.addPalm(x, z, kind)) return;
      }
    };
    if (!has('bush')) put('fiber');
    if (!has('palm')) put('wood');
    if (!has('rock')) put('rock');
  }

  // 海中サルベージ: 各島周囲の海底に沈没クレートを配置(潜水で発見・回収)
  placeCrates();
  computeRelicSpots(); // 瓶の手紙が示す「手がかりの海域」候補(独立シード=既存クレート配置は不変)

  // 大きな山の島と大きな島: placeCrates/relicSpotsの「後」に登録することで、
  // 既存クレートのid・座標・手紙スポットの決定論列を一切変えない(旧セーブ互換)。
  if (islandRes) {
    for (const bi of BIG_ISLES) {
      const t = buildMountainIsland(game.scene, bi.cx, bi.cz, bi);
      if (!t) continue;
      customIslands.push({ hf: t.hf, floorHf: buildFloorField(t.hf, SEABED_MIN), cx: bi.cx, cz: bi.cz, name: bi.name });
      // 資源(決定論): 山の島=岩中心 / 大きい島=椰子と草の広がり
      let s = bi.seed * 7919 + 17; const rnd = () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
      let rocks = 0, palms = 0, grass = 0;
      for (let i = 0; i < 2600 && (rocks < bi.rocks || palms < bi.palms || grass < bi.grass); i++) {
        const a = rnd() * Math.PI * 2, rr = (0.06 + 0.86 * rnd()) * bi.R;
        const x = bi.cx + Math.cos(a) * rr, z = bi.cz + Math.sin(a) * rr;
        const h = groundHeight(t.hf, x, z);
        if (!(h > SEA_LEVEL + 3)) continue;
        if (palms < bi.palms && h <= 60) { if (islandRes.addPalm(x, z, 'wood', h)) palms++; }
        else if (rocks < bi.rocks && h > bi.rockMinH) { if (islandRes.addRock(x, z, h, 3 + rnd() * 6)) rocks++; }
        else if (grass < bi.grass && h <= 130) { if (islandRes.addPalm(x, z, 'fiber', h)) grass++; }
      }
      console.log('[game] big isle "%s": rocks=%d palms=%d grass=%d peak=%d', bi.name, rocks, palms, grass, bi.peak);
    }
    placeBigIsleCrates();
  }

  // 一人称の手持ちアイテム(銛/斧)。所持していれば目線の右下に見える。
  heldRoot = spawnHeldItem(game.scene);

  // 初期いかだ: 開けた浜から海方向(見通し方向)へ進み、水に入った地点へ1台配置(spawn正面に見える)。
  {
    let bx = 200, bz = 300, ux = 0, uz = 1;
    if (spawnBeach) { bx = spawnBeach.x; bz = spawnBeach.z; ux = spawnBeach.sx; uz = spawnBeach.sz; }
    else { let b: ResourceNode | null = null; for (const n of nodes) if (!b || n.y < b.y) b = n; if (b) { bx = b.x; bz = b.z; const ox = b.x - 500, oz = b.z + 500, d = Math.hypot(ox, oz) || 1; ux = ox / d; uz = oz / d; } }
    let rx = bx + ux * 110, rz = bz + uz * 110;
    for (let dist = 22; dist <= 160; dist += 8) {
      const x = bx + ux * dist, z = bz + uz * dist;
      if (!(groundHeight(hf, x, z) > SEA_LEVEL)) { rx = x + ux * 70; rz = z + uz * 70; break; } // 波打ち際からさらに沖へ(見える距離)
    }
    const r0 = spawnRaft(game.scene, rx, rz, { size: 1, y: RAFT_FLOAT, yaw: Math.atan2(ux, uz) });
    if (r0) { (r0 as any).__probe = acquireWaveProbe(); rafts.push(r0); } // 初期いかだも乗れるように登録+波追従
  }

  loadFeats(); // 実績は周回をまたいで永続(buildHudの一覧生成より先に読む)
  hud.buildHud({ onCraft: (id) => window.__craftItem(id), onRestart: restart });
  hud.setHotbarHandlers((item) => equip(item)); // ホットバータップで装備切替

  // タイトル画面: 背景でカメラが島を周回するシネマティック。「はじめる/続きから」で開始。
  // ヘッドレステスト互換: localStorage['side6-skip-title'] があればタイトルを飛ばし従来フローに。
  const saved = loadState();
  const skipTitle = (() => { try { return !!localStorage.getItem('side6-skip-title'); } catch { return false; } })();
  const beginNew = () => {
    clearSave(); try { localStorage.removeItem(PROG_KEY); } catch { /* ignore */ } spawnOnBeach(); titleMode = false; player.frozen = false;
    clock.day = 1; clock.t = 0.42 * clock.dayLength; // タイトル放置で進んだ時計を戻し、必ずDay1・朝10時から始める
    hud.updateHotbar(); hud.toast('無人島に漂着した… 生き延びろ');
    hud.toast('🎯 沖の難破船を直して脱出しよう（コンパスが導く）');
  };
  const beginContinue = () => {
    if (!saved) return beginNew();
    applyState(saved, player, nodes, repair); restorePlaced(saved.placed); syncAssets(); loadProgress();
    restoreRelics(); // 読んだ手紙ぶんの❓海域を復元(開封済みは畳む)
    if (player.pos.y < -EYE) stats.oxygen = stats.maxOxygen; // 水中セーブ(修正前の旧セーブ)の救済: 低O2で目覚めて溺れないよう満タンで再開
    for (const c of crates) if (openedCrates.includes(c.id)) { c.opened = true; if (c.obj) c.obj.visible = false; } // 開封済みを畳む
    titleMode = false; player.frozen = false;
    hud.updateHotbar(); hud.toast(`続きから（Day ${clock.day}）`);
  };
  if (skipTitle) {
    if (saved) beginContinue(); else beginNew();
  } else {
    titleMode = true; player.frozen = true; // 周回カメラはtickが担当
    hud.showTitle(saved ? saved.clock.day : null, { onStart: beginNew, onContinue: beginContinue });
  }
  hud.updateHotbar();

  setupInput();
  setupTouch();
  initAudio();
  engageCamera();
  requestAnimationFrame(tick);
  window.__gameReady = true;
  console.log('[game] ready: island hf=%o nodes=%d saved=%o', !!hf, nodes.length, !!saved);
}

function whenReady(cb: () => void): void {
  const el = document.getElementById('initial-loading');
  // start() は最大1回だけ呼ぶ。MutationObserver と 65秒フォールバックの両方が発火しても、
  // 二重起動(HUD/#game-input/#start/tick の二重生成→入力レイヤが画面を覆って操作不能)を防ぐ。
  let started = false;
  const fire = () => { if (started) return; started = true; cb(); };
  const waitGame = () => {
    if (started) return;
    if (window.__game && window.__game.renderer && window.__game.scene) fire();
    else setTimeout(waitGame, 50);
  };
  if (!el || el.classList.contains('hidden')) { waitGame(); return; }
  let fb = 0;
  const mo = new MutationObserver(() => { if (el.classList.contains('hidden')) { mo.disconnect(); clearTimeout(fb); waitGame(); } });
  mo.observe(el, { attributes: true, attributeFilter: ['class'] });
  fb = window.setTimeout(() => { mo.disconnect(); waitGame(); }, 65000);
}

whenReady(start);
